/**
 * Email Thread Slide-over Alpine.js Component
 *
 * Opens when clicking an email in the inbox.
 * Shows thread header, messages in chronological order, and reply bar.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'
import flatpickr from "flatpickr"

// Addresses a thread-level Reply never answers: delivery machinery and
// unattended mailboxes. Mirrors EmailThread::NON_COUNTERPARTY_SENDERS.
const NON_COUNTERPARTY_SENDERS = /^(mailer-daemon|postmaster|no-?reply)@/i

export function emailThreadSlideover() {
  return {
    ...notesMixin,
    open: false,
    loading: false,
    error: null,
    threadId: null,
    thread: null,
    messages: [],
    entityLinks: [],
    // AI work-item summaries for this thread/its entities — collapsed behind
    // "Show me a summary" so the story is on request, not ambient
    workItems: [],
    // The repairs this thread asks for, found by the classifier ~19 seconds
    // after each message lands (the matter book is hours behind — median 3.3,
    // p90 29 — so the doorway cannot wait for it). One row each in "Go to".
    jobCandidates: [],
    summaryOpen: false,
    proposedActions: [],
    _proposalMode: null,
    // Internal notes on the thread — agency-only, never sent to participants
    notes: [],
    noteText: '',
    noteSaving: false,
    noteInputOpen: false,
    // "Go to" — the linked-records dropdown (rows + the link picker at its foot)
    goToOpen: false,
    // The Reply split-button's caret menu (Reply to all / Reply / Forward)
    replyMenuOpen: false,
    // Which verb the open composer is writing under. composerMode can't say:
    // reply and reply-all share mode 'reply' and differ only by their Cc list.
    // The bar stays put while you write, so it has to name the verb in play.
    composerVerb: 'reply',
    // "Create a maintenance job" — the property id whose prefill is in flight
    // (one row per linked property, so the spinner has to know which)
    creatingJob: null,
    // Filing doorways — one per analysed document we are certain about. Empty
    // is the common case and means exactly that: nothing certain to offer.
    filingDoorways: [],
    // The website's valuation form read back on arrival — see
    // Inbox::ValuationLead. null on every other thread.
    valuationDoorway: null,
    // Whoever wrote through the website, on any of its forms (Inbox::WebsiteFormLead):
    // the envelope says "Phillip James Website", the person is in the body.
    websiteLead: null,
    // True while /valuation_prefill is reading the thread for "Create valuation".
    valuationPrefilling: false,
    // "Suggest a response" — the ask-first popup and what was typed into it
    suggestOpen: false,
    suggestInstruction: '',
    // Summarise — the on-demand thread summary and the shared "working" state.
    // These calls take a few seconds against the dossier, so the wait is shown
    // as a skeleton rather than a spinner on a button.
    summarySections: null,
    summaryMeta: null,
    // The set of emails this arrival calls for. Cards stay until sent or
    // dismissed — see reviewSuggestion.
    suggestions: null,
    usedSteer: '',
    _openSuggestionKey: null,
    aiWorking: false,
    aiWorkingLabel: '',
    // Manage links — removing a link is a correction, so it is a deliberate
    // mode rather than an X sitting one stray click from every row you meant
    // to open. Resets whenever the panel closes.
    managingLinks: false,
    linkRemoving: null,
    // Manual entity linking — the "Link a record" picker in the Go to dropdown
    linkPickerOpen: false,
    linkQuery: '',
    linkResults: [],
    linkSearching: false,
    linkAdding: false,
    _linkSearchTimeout: null,
    currentUserId: null,
    // Snooze dropdown (bell button in the actions bar)
    snoozeMenuOpen: false,
    snoozing: false,
    snoozeCustomDate: null,
    composerOpen: false,
    focusReplyOnInit: false,
    replyDraft: '',
    draftInstruction: '',
    drafting: false,
    sending: false,
    attachedFiles: [],
    composing: false,
    composerMode: 'reply',
    composerTo: '',
    composerToRecipients: [],
    composerCc: '',
    composerCcRecipients: [],
    composerBcc: '',
    composerBccRecipients: [],
    composerSubject: '',
    showCcBcc: false,
    // Which recipient field the typeahead dropdown is currently bound to.
    // Drives where selectContact() pushes the chip.
    activeRecipientField: 'to',
    signatureExpanded: false,
    archiveAfterSend: false,
    showSendMenu: false,
    // The schedule popover (inbox/schedule_menu) — opened from the composer's
    // Send caret (scheduleMenuOpen) or a scheduled message's chip
    // (messageScheduleMenuId). One view state and one picked date serve
    // both, since only one can be open at a time.
    scheduleMenuOpen: false,
    messageScheduleMenuId: null,
    scheduleMenuStyle: '',
    scheduleView: 'presets',
    scheduleCustomDate: null,
    schedulingSend: false,
    reschedulingId: null,
    contactResults: [],
    groupResults: [],
    showContactDropdown: false,
    searchingContacts: false,
    selectedContacts: [],
    _contactSearchTimeout: null,
    _contactPropertyCache: {},
    // Group recipients modal — opened by clicking a group chip
    groupModalOpen: false,
    groupModalChipIdx: null,
    groupModalLoading: false,
    groupModalSearch: '',
    _groupModalSnapshot: null,
    // Mass email send confirmation
    sendConfirmOpen: false,
    sendConfirmCount: 0,
    sendingMassEmail: false,
    matchedProperties: [],
    _subjectMatchedProperty: null,
    _propertySearchTimeout: null,
    emailSignature: '',
    accountEmail: '',
    zIndex: 100010,
    // Draft persistence
    currentDraftId: null,
    savingDraft: false,
    draftGuardOpen: false,
    userEditedDraft: false,
    // Autosave: the composer persists itself continuously, Gmail-style.
    // draftDirty = user edits not yet on the server. The revision counter
    // detects edits that land while a save is in flight; the epoch counter
    // ties in-flight responses to the composer session they belong to, so
    // a late response can't resurrect a draft id into the next email.
    draftDirty: false,
    _draftRevision: 0,
    _flushedRevision: -1,
    _draftEpoch: 0,
    _autosaveTimer: null,
    _saveChain: null,
    _suppressUserEdited: false,

    // Post-send callback wiring — used when the composer is opened pre-filled
    // from another module (e.g. quote → landlord email) and we need to mark
    // the originating record as "sent" once the email has gone out.
    _linkedSendCompletion: null,
    // A send_new_email doorway card pre-filled this compose — the send
    // carries the id back so the card resolves via the real path.
    _composeProposedActionId: null,

    init() {
      this.emailSignature = this.$root.dataset.emailSignature || ''
      this.currentUserId = parseInt(this.$root.dataset.userId, 10) || null
      this.accountEmail = (this.$root.dataset.accountEmail || '').toLowerCase()
      // Store bound references so we can remove them on destroy
      this._onOpenThread = (e) => this.openThread(e.detail.id)
      this._onOpenCompose = (e) => this.openComposePreFilled(e.detail)
      this._onProposalEdit = (e) => this.editProposalInComposer(e.detail)
      this._onProposalDecided = (e) => this.handleProposalDecided(e.detail)
      // A job saved from one of this thread's repair rows — mark it raised.
      this._onJobSaved = (e) => {
        if (e.detail?.emailJobCandidateId) {
          this.onJobCandidateRaised(e.detail.emailJobCandidateId, e.detail.id)
        }
      }
      this._scrollRAF = null
      this._onMessage = (e) => {
        if (e.data?.type === 'email-iframe-resize' && e.data.height > 0 && e.data.id) {
          // Match by data-iframe-id attribute instead of contentWindow reference,
          // which is unreliable across browsers and timing conditions.
          const iframe = this.$root.querySelector(`iframe[data-iframe-id="${e.data.id}"]`)
          if (iframe) iframe.style.height = e.data.height + 'px'
        }
        // Scroll the parent container when text is being selected inside an iframe
        // and the mouse is near the top/bottom edge of the scroll container.
        // The iframe posts screenY (absolute screen coords) because its own
        // viewport is the full email height, not the visible portion.
        if (e.data?.type === 'email-iframe-scroll') {
          const container = this.$refs.messagesContainer
          if (!container) return
          if (e.data.direction === 0) {
            if (this._scrollRAF) { cancelAnimationFrame(this._scrollRAF); this._scrollRAF = null }
            return
          }
          const edge = 50
          const rect = container.getBoundingClientRect()
          // Convert screenY to client Y (screenY - window.screenY - browser chrome offset)
          const clientY = e.data.screenY - window.screenY - (window.outerHeight - window.innerHeight)
          const distFromTop = clientY - rect.top
          const distFromBottom = rect.bottom - clientY
          let dir = 0, speed = 0
          if (distFromTop < edge) {
            dir = -1; speed = Math.ceil((edge - distFromTop) / 5)
          } else if (distFromBottom < edge) {
            dir = 1; speed = Math.ceil((edge - distFromBottom) / 5)
          }
          if (dir === 0) {
            if (this._scrollRAF) { cancelAnimationFrame(this._scrollRAF); this._scrollRAF = null }
            return
          }
          if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF)
          const step = () => {
            container.scrollTop += dir * speed
            this._scrollRAF = requestAnimationFrame(step)
          }
          this._scrollRAF = requestAnimationFrame(step)
        }
      }

      this._onOpenDraft = (e) => this.loadDraft(e.detail)
      // Last-chance draft flushes. Turbo visits keep the document alive, so
      // a normal fetch completes; pagehide/tab-hide may tear the page down,
      // so those flush with keepalive to survive it.
      this._onTurboVisit = () => this.flushDraft()
      this._onPageExit = () => this.flushDraft({ keepalive: true })
      this._onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') this.flushDraft({ keepalive: true })
      }
      window.addEventListener('open-email-thread', this._onOpenThread)
      window.addEventListener('open-email-compose', this._onOpenCompose)
      window.addEventListener('proposed-action:edit-in-composer', this._onProposalEdit)
      window.addEventListener('proposed-action:decided', this._onProposalDecided)
      window.addEventListener('job-saved', this._onJobSaved)
      window.addEventListener('open-email-draft', this._onOpenDraft)
      window.addEventListener('message', this._onMessage)
      window.addEventListener('turbo:before-visit', this._onTurboVisit)
      window.addEventListener('pagehide', this._onPageExit)
      document.addEventListener('visibilitychange', this._onVisibilityChange)

      // Live-flip message chips (sending → sent / send_failed) when the
      // background send job finishes. EmailMessage#broadcast_status_update
      // publishes to DataUpdatesChannel on every status transition.
      const self = this
      this._subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            // Proposal cards live-update (another user approving/dismissing,
            // the agent withdrawing) — flip in place, remove on destroy.
            if (message.type === 'proposed_action') {
              const data = message.data || {}
              if (!(data.subject_type === 'EmailThread' && String(data.subject_id) === String(self.threadId))) return
              if (message.action === 'destroy') {
                self.proposedActions = self.proposedActions.filter(r => r.id !== message.id)
              } else {
                const idx = self.proposedActions.findIndex(r => r.id === message.id)
                if (idx >= 0) Object.assign(self.proposedActions[idx], data)
                else if (data.status === 'proposed') self.proposedActions.push(data)
              }
              return
            }
            if (message.type !== 'email_message') return
            if (!self.threadId || String(self.threadId) !== String(message.email_thread_id)) return
            self.applyMessageStatusUpdate(message)
          }
        }
      )
    },

    destroy() {
      this.flushDraft({ keepalive: true }) // teardown paths that bypass turbo:before-visit
      window.removeEventListener('open-email-thread', this._onOpenThread)
      window.removeEventListener('open-email-compose', this._onOpenCompose)
      window.removeEventListener('proposed-action:edit-in-composer', this._onProposalEdit)
      window.removeEventListener('proposed-action:decided', this._onProposalDecided)
      window.removeEventListener('job-saved', this._onJobSaved)
      window.removeEventListener('open-email-draft', this._onOpenDraft)
      window.removeEventListener('message', this._onMessage)
      window.removeEventListener('turbo:before-visit', this._onTurboVisit)
      window.removeEventListener('pagehide', this._onPageExit)
      document.removeEventListener('visibilitychange', this._onVisibilityChange)
      clearTimeout(this._autosaveTimer)
      if (this._scrollRAF) { cancelAnimationFrame(this._scrollRAF); this._scrollRAF = null }
      this._subscription?.unsubscribe()
      this._subscription = null
    },

    // Merge a server-pushed status update into the matching message so the
    // "Sending…" chip flips to "Sent" (or "Failed, N attempts") without a
    // refresh. Splice rather than mutate in place so Alpine sees the change.
    applyMessageStatusUpdate(broadcast) {
      const idx = this.messages.findIndex(m => String(m.id) === String(broadcast.id))
      if (idx === -1) return

      const current = this.messages[idx]
      this.messages.splice(idx, 1, {
        ...current,
        status: broadcast.data.status,
        send_error: broadcast.data.send_error,
        send_attempts: broadcast.data.send_attempts,
        external_message_id: broadcast.data.external_message_id || current.external_message_id,
        sent_at: broadcast.data.sent_at || current.sent_at,
        scheduled_for: broadcast.data.scheduled_for ?? current.scheduled_for
      })
    },

    // The filing doorway closes the moment the certificate saves: the chip
    // flips from "File certificate" to "Certificate filed" without a reload,
    // so the button that filed this document can't be clicked again. One
    // document, one certificate — duplicates breed duplicate reminders and
    // jobs. Splice rather than mutate in place so Alpine sees the change.
    markAttachmentFiled({ email_attachment_id, certificate_id } = {}) {
      if (!email_attachment_id) return

      this.messages.forEach((msg, idx) => {
        if (!Array.isArray(msg.attachments)) return

        let changed = false
        const attachments = msg.attachments.map(att => {
          if (String(att.email_attachment_id) !== String(email_attachment_id) || !att.certificate) return att

          changed = true
          return { ...att, certificate: { ...att.certificate, certificate_id: certificate_id } }
        })
        if (changed) this.messages.splice(idx, 1, { ...msg, attachments })
      })
    },

    openThread(id) {
      if (id === 'compose') {
        this.openCompose()
        return
      }
      // A different thread is about to replace the composer — persist any
      // unsaved writing first (flushDraft captures the payload before the
      // wipe below), then start a fresh draft session. Clearing the draft id
      // matters: leaving it set made the next reply save OVER the old draft.
      this.flushDraft()
      this._resetDraftTracking()
      this.currentDraftId = null
      this.userEditedDraft = false
      this.threadId = id
      this.composing = false
      this.thread = null
      this.messages = []
      this.entityLinks = []
      this.workItems = []
      this.jobCandidates = []
      this.summaryOpen = false
      this.goToOpen = false
      this.replyMenuOpen = false
      this.closeLinkPicker()
      this.proposedActions = []
      this._proposalMode = null
      this.notes = []
      this.noteText = ''
      this.noteInputOpen = false
      this.closeSnoozeMenu()
      this.setReplyDraft('')
      this.attachedFiles = []
      this.composerMode = 'reply'
      this.composerOpen = false
      this.composerTo = ''
      this.composerToRecipients = []
      this.composerCc = ''
      this.composerCcRecipients = []
      this.composerBcc = ''
      this.composerBccRecipients = []
      this.composerSubject = ''
      this.showCcBcc = false
      this.error = null
      // Ensure this slideover opens on top of any others
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }
      this.toggleAiFab(false)
      this.fetchThread()
    },

    openCompose() {
      // Same guard as openThread: save whatever is in the composer before
      // wiping it for the new email, and detach from any previous draft so
      // this compose can't overwrite it.
      this.flushDraft()
      this._resetDraftTracking()
      this.currentDraftId = null
      this.userEditedDraft = false
      this._proposalMode = null
      this._composeProposedActionId = null
      this._composeAbout = null
      this.threadId = null
      this.composing = true
      this.thread = null
      this.messages = []
      this.entityLinks = []
      this.workItems = []
      this.jobCandidates = []
      this.summaryOpen = false
      this.goToOpen = false
      this.replyMenuOpen = false
      this.closeLinkPicker()
      this.proposedActions = []
      this._proposalMode = null
      this.notes = []
      this.noteText = ''
      this.noteInputOpen = false
      this.loading = false
      this.error = null
      this.setReplyDraft('')
      this.attachedFiles = []
      this.composerMode = 'compose'
      this.composerOpen = true
      this.closeGoTo()
      this.composerTo = ''
      this.composerToRecipients = []
      this.composerCc = ''
      this.composerCcRecipients = []
      this.composerBcc = ''
      this.composerBccRecipients = []
      this.composerSubject = ''
      this.showCcBcc = false
      this.selectedContacts = []
      this.matchedProperties = []
      this._contactPropertyCache = {}
      this._subjectMatchedProperty = null
      this.contactResults = []
      this.showContactDropdown = false
      // Ensure this slideover opens on top of any others
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }
      this.open = true
      this.toggleAiFab(false)
      this.$nextTick(() => this.$refs.composeToInput?.focus())
    },

    // Open compose pre-filled from an email button click.
    //
    // Single-contact form (legacy): { to, name, type, id, subject }
    // Rich form (used by Send-to-Landlord): {
    //   to: [{ email, name, type, id }, ...],
    //   subject, body_html,
    //   attachments: [{ signed_blob_id, filename, byte_size, content_type }, ...],
    //   completion: { url, payload } — POSTed after a successful send,
    //     with `thread_id` merged into payload from the compose response
    // }
    openComposePreFilled(detail) {
      this.openCompose()
      this._linkedSendCompletion = detail.completion || null
      this._composeProposedActionId = detail.proposed_action_id || null
      // The record the email is about ({ type, id }), for Help me write
      this._composeAbout = detail.about || null

      if (detail.subject) {
        this.composerSubject = detail.subject
      }

      const recipients = Array.isArray(detail.to)
        ? detail.to
        : (detail.to ? [ { email: detail.to, name: detail.name, type: detail.type, id: detail.id } ] : [])

      recipients.forEach(r => {
        if (!r.email) return
        const contact = {
          kind: 'contact',
          email: r.email,
          name: r.name || r.email,
          type: r.type || null,
          id: r.id || null,
          // When set, the chip shows the actual email address (not just the
          // entity name). Used for e-signature sends where the recipient may
          // be an override and the agent must see exactly where it's going.
          showEmail: !!r.show_email
        }
        if (!this.composerToRecipients.some(c => c.email === contact.email)) {
          this.composerToRecipients.push(contact)
        }
        if (!this.selectedContacts.some(c => c.email === contact.email)) {
          this.selectedContacts.push(contact)
        }
        if (contact.type) {
          this.searchContactProperties(contact)
        }
      })

      if (detail.body_html) {
        // Synchronously, NOT on the next tick: the Trix editor's
        // trix-initialize does loadHTML(replyDraft) when it mounts, and
        // deferring meant the editor could initialise first, load the empty
        // string openCompose() had just set, and never see the body — a
        // pre-filled compose that opens blank.
        this.setReplyDraft(detail.body_html)
      }

      if (Array.isArray(detail.attachments)) {
        detail.attachments.forEach(a => {
          if (!a.signed_blob_id) return
          this.attachedFiles = [
            ...this.attachedFiles,
            {
              name: a.filename || 'attachment',
              size: a.byte_size || 0,
              file: null,
              // Pre-filled attachments have no File object, so the type
              // must travel with the descriptor or the send declares
              // application/octet-stream and the chips lose their PDF face.
              content_type: a.content_type || null,
              uploading: false,
              progress: 100,
              signed_blob_id: a.signed_blob_id,
              error: null
            }
          ]
        })
      }

      // Pre-filled content is user-initiated (they clicked an email button),
      // so it should persist like anything they typed themselves.
      this.touchDraft()

      // Already addressed: the cursor goes in the body, not the To field
      if (this.composerToRecipients.length) this._focusComposeBody()
    },

    // The cursor in the body's first run of empty lines, on the middle one, so
    // a greeting-and-sign-off draft is written with a blank line either side;
    // else at the start. After openCompose's own focus and the body's load
    // (both on the next tick); Trix may still be initialising, so it waits a
    // frame at a time for the editor.
    _focusComposeBody(attempt = 0) {
      this.$nextTick(() => requestAnimationFrame(() => {
        const el = this.$refs.composeTrixEditor
        if (!el?.editor) {
          if (attempt < 10) this._focusComposeBody(attempt + 1)
          return
        }
        const run = el.editor.getDocument().toString().match(/\n{2,}/)
        el.focus()
        el.editor.setSelectedRange(run ? run.index + Math.ceil((run[0].length - 1) / 2) : 0)
      }))
    },

    async close() {
      clearTimeout(this._autosaveTimer)
      // A compose opened from a suggested reply is a detour, not a
      // destination: closing it goes BACK to the thread rather than out to
      // the inbox. Re-opening by id rather than restoring a snapshot, so the
      // thread comes back with whatever just happened to it already in place.
      if (this._returnToThreadId && this.composing) {
        if (this.draftDirty && (this.currentDraftId || this.hasUnsavedContent)) {
          const saved = await this.saveDraft()
          if (!saved) return
        }
        const threadId = this._returnToThreadId
        this._returnToThreadId = null
        this.openThread(threadId)
        return
      }
      // Only save when there are edits autosave hasn't already persisted.
      // Wait for the save so a rejected draft keeps the composer open
      // instead of being wiped by _forceClose(). saveDraft() alerts on
      // failure; we abort the close so the user can fix it or copy their text.
      if (this.draftDirty && (this.currentDraftId || this.hasUnsavedContent)) {
        const saved = await this.saveDraft()
        if (!saved) return
      }
      this._forceClose()
    },

    _forceClose() {
      clearTimeout(this._autosaveTimer)
      this.open = false
      this.toggleAiFab(true)
      // Clear data after the slide-out animation (300ms) so the user
      // doesn't see "(no subject)" flash while the panel is closing.
      setTimeout(() => {
        if (this.open) return // re-opened before timeout
        this.composing = false
        this.threadId = null
        this.thread = null
        this.messages = []
        this.entityLinks = []
        this.notes = []
        this.noteText = ''
        this.noteInputOpen = false
        this.closeSnoozeMenu()
        this.closeScheduleMenu()
        this.closeMessageScheduleMenu()
        this.setReplyDraft('')
        this.attachedFiles = []
        this.composerMode = 'reply'
        this.composerOpen = false
        this.composerTo = ''
        this.composerToRecipients = []
        this.composerCc = ''
        this.composerCcRecipients = []
        this.composerBcc = ''
        this.composerBccRecipients = []
        this.composerSubject = ''
        this.showCcBcc = false
        this.currentDraftId = null
        this.userEditedDraft = false
        this.draftGuardOpen = false
        this.draftDirty = false
        this._flushedRevision = -1
        this._draftEpoch++
        this.selectedContacts = []
        this.matchedProperties = []
        this._contactPropertyCache = {}
        this._subjectMatchedProperty = null
        this._linkedSendCompletion = null
        this._composeProposedActionId = null
      }, 350)
    },

    toggleAiFab(visible) {
      const fab = document.querySelector('.ai-fab-container')
      if (!fab) return
      fab.style.transition = 'opacity 0.2s ease'
      if (visible) {
        fab.style.display = ''
        requestAnimationFrame(() => { fab.style.opacity = '1' })
      } else {
        fab.style.opacity = '0'
        setTimeout(() => { fab.style.display = 'none' }, 200)
      }
    },

    async archiveThread() {
      if (!this.threadId) return
      try {
        const id = this.threadId
        const resp = await fetch(`/inbox/${id}/archive`, {
          method: 'PATCH',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content, 'Accept': 'application/json' }
        })
        if (resp.ok) {
          this.close()
          window.dispatchEvent(new CustomEvent('remove-email-thread', { detail: { id } }))
        }
      } catch (e) { console.error('Archive failed:', e) }
    },

    async deleteThread() {
      if (!this.threadId) return
      try {
        const id = this.threadId
        const resp = await fetch(`/inbox/${id}`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content, 'Accept': 'application/json' }
        })
        if (resp.ok) {
          this.close()
          window.dispatchEvent(new CustomEvent('remove-email-thread', { detail: { id } }))
        }
      } catch (e) { console.error('Delete failed:', e) }
    },

    async updateSubject(newSubject) {
      if (!this.threadId) return
      const trimmed = newSubject?.trim() || null
      // Skip if unchanged
      if ((trimmed || null) === (this.thread?.custom_subject || null)) return
      try {
        const resp = await fetch(`/inbox/${this.threadId}/update_subject`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ custom_subject: trimmed })
        })
        if (resp.ok) {
          const data = await resp.json()
          this.thread.subject = data.subject
          this.thread.custom_subject = trimmed
        }
      } catch (e) { console.error('Update subject failed:', e) }
    },

    async markUnread() {
      if (!this.threadId) return
      try {
        const id = this.threadId
        const resp = await fetch(`/inbox/${id}/mark_unread`, {
          method: 'PATCH',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content, 'Accept': 'application/json' }
        })
        if (resp.ok) {
          this.close()
        }
      } catch (e) { console.error('Mark unread failed:', e) }
    },

    // --- Snooze ---

    toggleSnoozeMenu() {
      if (this.snoozeMenuOpen) {
        this.closeSnoozeMenu()
      } else {
        this.snoozeMenuOpen = true
        this.$nextTick(() => this.initSnoozePicker())
      }
    },

    closeSnoozeMenu() {
      this.snoozeMenuOpen = false
      this._snoozePicker?.destroy()
      this._snoozePicker = null
      this.snoozeCustomDate = null
    },

    initSnoozePicker() {
      this._snoozePicker?.destroy()
      const input = this.$refs.snoozeCustomInput
      if (!input) return
      this._snoozePicker = this._timePicker(input, (date) => { this.snoozeCustomDate = date })
    },

    // One date & time picker for every "pick a moment" menu here — snooze,
    // send later, move a scheduled send.
    _timePicker(input, onChange) {
      return flatpickr(input, {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        altInput: true,
        altFormat: 'j M Y \\a\\t H:i',
        time_24hr: true,
        appendTo: document.body,
        minDate: 'today',
        defaultHour: 9,
        defaultMinute: 0,
        minuteIncrement: 15,
        onChange: (selectedDates) => onChange(selectedDates[0] || null)
      })
    },

    // --- Scheduled send ---

    // Mass emails and edits of Alice's proposals only send now.
    get canSchedule() {
      return !this.hasGroupChips && !this._proposalMode
    },

    // The popover is inserted (x-if) during the click that opens it, so its
    // click-outside listener joins `document` while that same click is still
    // bubbling — and would close it at once. Remember the opening event and
    // let the close handlers ignore exactly that one.
    toggleScheduleMenu(event) {
      if (this.scheduleMenuOpen) return this.closeScheduleMenu()
      this.closeMessageScheduleMenu()
      this._resetScheduleView()
      this._scheduleOpeningEvent = event
      this._anchorScheduleMenu(event.currentTarget.closest('[data-send-menu]'))
      this.scheduleMenuOpen = true
    },

    closeScheduleMenu(event) {
      if (event && event === this._scheduleOpeningEvent) return
      this.scheduleMenuOpen = false
      this._resetScheduleView()
    },

    _resetScheduleView() {
      this._schedulePicker?.destroy()
      this._schedulePicker = null
      this.scheduleView = 'presets'
      this.scheduleCustomDate = null
      this._releaseScheduleAnchor()
    },

    // The popover lives on <body> (x-teleport) and is positioned `fixed`
    // from whatever opened it, so nothing in the slideover can clip it. It
    // hugs the anchor's near edge and grows away from it: below when there
    // is room for the calendar view, above otherwise. Re-placed on scroll
    // and resize while open so it stays attached.
    _anchorScheduleMenu(anchor) {
      this._releaseScheduleAnchor()
      this._scheduleAnchor = anchor
      this._placeScheduleMenu()
      this._onScheduleReposition = () => this._placeScheduleMenu()
      window.addEventListener('resize', this._onScheduleReposition)
      document.addEventListener('scroll', this._onScheduleReposition, true)
    },

    _releaseScheduleAnchor() {
      if (this._onScheduleReposition) {
        window.removeEventListener('resize', this._onScheduleReposition)
        document.removeEventListener('scroll', this._onScheduleReposition, true)
        this._onScheduleReposition = null
      }
      this._scheduleAnchor = null
    },

    _placeScheduleMenu() {
      const anchor = this._scheduleAnchor
      if (!anchor?.isConnected) return
      const rect = anchor.getBoundingClientRect()
      const width = Math.min(340, window.innerWidth - 16)
      const gap = 6
      const edge = 8
      const left = Math.max(edge, Math.min(rect.right - width, window.innerWidth - width - edge))
      const below = window.innerHeight - rect.bottom - gap - edge
      const above = rect.top - gap - edge
      const CALENDAR_VIEW = 480
      const openBelow = below >= CALENDAR_VIEW || below >= above
      const maxHeight = Math.max(160, openBelow ? below : above)
      const vertical = openBelow
        ? `top:${Math.round(rect.bottom + gap)}px`
        : `bottom:${Math.round(window.innerHeight - rect.top + gap)}px`
      this.scheduleMenuStyle = `left:${Math.round(left)}px; ${vertical}; max-height:${Math.round(maxHeight)}px; z-index:${(this.zIndex || 100000) + 30}`
    },

    // Swap the popover to the calendar. It renders inline inside the popover
    // (nothing floats separately, so there is nothing else to dismiss) and
    // starts on `defaultIso` — the message's current time when moving one,
    // otherwise tomorrow morning — so the confirm button is live at once.
    openCustomSchedule(trigger, defaultIso) {
      const menu = trigger?.closest('[data-schedule-menu]')
      const start = defaultIso ? new Date(defaultIso) : this._tomorrowMorning()
      this.scheduleCustomDate = start
      this.scheduleView = 'custom'
      this.$nextTick(() => {
        this._schedulePicker?.destroy()
        const container = menu?.querySelector('[data-schedule-calendar]')
        const input = container?.querySelector('[data-schedule-input]')
        if (!input) return
        this._schedulePicker = flatpickr(input, {
          inline: true,
          appendTo: container,
          enableTime: true,
          time_24hr: true,
          dateFormat: 'Y-m-d H:i',
          // A Date (not 'today') so the time is bounded as well as the day —
          // 'today' would let 09:00 through at half past two.
          minDate: new Date(),
          defaultDate: start,
          minuteIncrement: 15,
          // flatpickr edits the time by mutating the SAME Date object, and
          // Alpine ignores re-assigning an unchanged reference — so copy it,
          // or the read-back and the confirm button judge a stale moment.
          // onValueUpdate covers typing in the hour/minute fields, which
          // does not always raise onChange.
          onChange: (selectedDates) => this._setScheduleCustomDate(selectedDates[0]),
          onValueUpdate: (selectedDates) => this._setScheduleCustomDate(selectedDates[0])
        })
      })
    },

    _setScheduleCustomDate(date) {
      this.scheduleCustomDate = date ? new Date(date.getTime()) : null
    },

    // The picker bounds the time, but the hour/minute fields can still be
    // typed into — so the confirm button checks the moment itself.
    get scheduleCustomInPast() {
      return !!this.scheduleCustomDate && this.scheduleCustomDate.getTime() <= Date.now()
    },

    backToSchedulePresets() {
      this._schedulePicker?.destroy()
      this._schedulePicker = null
      this.scheduleView = 'presets'
      this.scheduleCustomDate = null
    },

    _tomorrowMorning() {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      d.setHours(9, 0, 0, 0)
      return d
    },

    // Quick picks for a later send. "Later today" is the next round hour at
    // least two hours off, offered while that is still a working hour today.
    // "Monday morning" drops out when tomorrow already is Monday.
    get schedulePresets() {
      const now = new Date()
      const at = (dayOffset, hour) => {
        const d = new Date(now)
        d.setDate(now.getDate() + dayOffset)
        d.setHours(hour, 0, 0, 0)
        return d
      }
      const preset = (label, date) => ({ label, detail: this.formatSnoozeTime(date), date })
      const presets = []

      const laterToday = new Date(now.getTime() + 2 * 60 * 60 * 1000)
      laterToday.setMinutes(0, 0, 0)
      laterToday.setHours(laterToday.getHours() + 1)
      if (laterToday.toDateString() === now.toDateString() && laterToday.getHours() <= 18) {
        presets.push(preset('Later today', laterToday))
      }

      presets.push(preset('Tomorrow morning', at(1, 9)))
      presets.push(preset('Tomorrow afternoon', at(1, 14)))

      const daysToMonday = ((8 - now.getDay()) % 7) || 7
      if (daysToMonday !== 1) presets.push(preset('Monday morning', at(daysToMonday, 9)))

      return presets
    },

    scheduleSend(date) {
      if (!date) return
      this.closeScheduleMenu()
      return this.sendReply({ scheduledFor: date })
    },

    toggleMessageScheduleMenu(msg, event) {
      if (this.messageScheduleMenuId === msg.id) return this.closeMessageScheduleMenu()
      this.closeScheduleMenu()
      this._resetScheduleView()
      this._scheduleOpeningEvent = event
      this._anchorScheduleMenu(event.currentTarget)
      this.messageScheduleMenuId = msg.id
    },

    closeMessageScheduleMenu(event) {
      if (event && event === this._scheduleOpeningEvent) return
      this.messageScheduleMenuId = null
      this._resetScheduleView()
    },

    // Move a booked send to `when` (a Date) or send it now ('now').
    async rescheduleMessage(msg, when) {
      if (!when || this.reschedulingId) return
      this.reschedulingId = msg.id
      try {
        const resp = await fetch(`/inbox/${this.threadId}/scheduled/${msg.id}`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ scheduled_for: when === 'now' ? 'now' : when.toISOString() })
        })
        const data = await resp.json()
        if (resp.ok && data.success) {
          this.closeMessageScheduleMenu()
          const idx = this.messages.findIndex(m => String(m.id) === String(msg.id))
          if (idx !== -1) this.messages.splice(idx, 1, { ...this.messages[idx], ...data.message })
          window.dispatchEvent(new CustomEvent('scheduled-changed', {
            detail: { delta: when === 'now' ? -1 : 0, messageId: msg.id }
          }))
        } else {
          alert(data.error || 'Could not change the send time')
        }
      } catch (e) {
        console.error('Reschedule failed:', e)
        alert('Could not change the send time. Please try again.')
      }
      this.reschedulingId = null
    },

    // Take a booked send off the schedule. It comes back as a draft, which
    // opens straight away so the person can change it, resend or discard.
    async unscheduleMessage(msg) {
      if (this.reschedulingId) return
      this.reschedulingId = msg.id
      try {
        const resp = await fetch(`/inbox/${this.threadId}/scheduled/${msg.id}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json'
          }
        })
        const data = await resp.json()
        if (resp.ok && data.success) {
          this.closeMessageScheduleMenu()
          this.messages = this.messages.filter(m => String(m.id) !== String(msg.id))
          window.dispatchEvent(new CustomEvent('scheduled-changed', { detail: { delta: -1, messageId: msg.id } }))
          window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: 1 } }))
          if (data.thread_removed) {
            // The thread existed only to carry this email — it went with it.
            const removedId = this.threadId
            this._forceClose()
            window.dispatchEvent(new CustomEvent('remove-email-thread', { detail: { id: removedId } }))
          }
          this.loadDraft(data.draft)
        } else {
          alert(data.error || 'Could not cancel the send')
        }
      } catch (e) {
        console.error('Unschedule failed:', e)
        alert('Could not cancel the send. Please try again.')
      }
      this.reschedulingId = null
    },

    // Quick-pick wake times. "Later today" only offers itself while it still
    // lands today; after ~9pm the list starts at Tomorrow.
    get snoozePresets() {
      const now = new Date()
      const presets = []

      const laterToday = new Date(now.getTime() + 3 * 60 * 60 * 1000)
      laterToday.setMinutes(Math.ceil(laterToday.getMinutes() / 15) * 15, 0, 0)
      if (laterToday.toDateString() === now.toDateString()) {
        presets.push({ label: 'Later today', detail: this.formatSnoozeTime(laterToday), date: laterToday })
      }

      const tomorrow = new Date(now)
      tomorrow.setDate(now.getDate() + 1)
      tomorrow.setHours(9, 0, 0, 0)
      presets.push({ label: 'Tomorrow', detail: this.formatSnoozeTime(tomorrow), date: tomorrow })

      const nextWeek = new Date(now)
      nextWeek.setDate(now.getDate() + (((8 - now.getDay()) % 7) || 7))
      nextWeek.setHours(9, 0, 0, 0)
      presets.push({ label: 'Next week', detail: this.formatSnoozeTime(nextWeek), date: nextWeek })

      return presets
    },

    // "Today, 17:45" / "Tomorrow, 09:00" / "Mon 16 Jun, 09:00"
    formatSnoozeTime(dateOrIso) {
      const date = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso)
      const now = new Date()
      const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      const tomorrow = new Date(now)
      tomorrow.setDate(now.getDate() + 1)
      if (date.toDateString() === now.toDateString()) return `Today, ${time}`
      if (date.toDateString() === tomorrow.toDateString()) return `Tomorrow, ${time}`
      const day = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
      return `${day}, ${time}`
    },

    async snoozeThread(date) {
      if (!this.threadId || this.snoozing || !date) return
      this.snoozing = true
      try {
        const resp = await fetch(`/inbox/${this.threadId}/snooze`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ until: date.toISOString() })
        })
        const data = await resp.json()
        if (resp.ok && data.success) {
          const id = this.threadId
          const wasSnoozed = !!this.thread?.snoozed_until
          this.closeSnoozeMenu()
          this._forceClose()
          window.dispatchEvent(new CustomEvent('remove-email-thread', { detail: { id } }))
          if (!wasSnoozed) {
            window.dispatchEvent(new CustomEvent('email-thread-snoozed', { detail: { id, delta: 1 } }))
          }
        } else {
          alert(data.error || 'Failed to snooze')
        }
      } catch (e) {
        console.error('Snooze failed:', e)
        alert('Failed to snooze. Please try again.')
      }
      this.snoozing = false
    },

    snoozeCustom() {
      if (this.snoozeCustomDate) this.snoozeThread(this.snoozeCustomDate)
    },

    async unsnoozeThread() {
      if (!this.threadId || this.snoozing) return
      this.snoozing = true
      try {
        const resp = await fetch(`/inbox/${this.threadId}/unsnooze`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json'
          }
        })
        if (resp.ok) {
          if (this.thread) this.thread.snoozed_until = null
          this.closeSnoozeMenu()
          window.dispatchEvent(new CustomEvent('email-thread-snoozed', { detail: { id: this.threadId, delta: -1 } }))
        }
      } catch (e) { console.error('Unsnooze failed:', e) }
      this.snoozing = false
    },

    // --- Internal notes ---

    // Messages and internal notes merged into one chronological list, so a
    // note renders at the position in the conversation where it was written.
    // Reuses the existing objects (no spreading) — new object identities per
    // evaluation would re-trigger the iframe :srcdoc bindings and reload
    // every message body.
    get threadTimeline() {
      const ts = (item) => {
        const date = item._isNote ? item.created_at : (item.sent_at || item.created_at)
        // Outbound messages still sending have no sent_at yet — keep them at
        // the bottom of the thread (they're the newest thing in it).
        return date ? new Date(date).getTime() : Infinity
      }
      return [...this.messages, ...this.notes].sort((a, b) => ts(a) - ts(b))
    },

    toggleNoteInput() {
      this.noteInputOpen = !this.noteInputOpen
      if (this.noteInputOpen) {
        // The composer renders inline at the bottom of the thread (where the
        // note will appear), so bring it into view before focusing.
        this.$nextTick(() => {
          this.$refs.noteInput?.focus({ preventScroll: true })
          this.$refs.noteInput?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      }
    },

    async addNote() {
      const content = this.noteText.trim()
      if (!content || this.noteSaving || !this.threadId) return
      this.noteSaving = true
      try {
        const note = await this.saveNote({ type: 'EmailThread', id: this.threadId }, content)
        this.notes.push({ ...note, _key: 'note_' + note.id, _isNote: true })
        this.noteText = ''
        this.noteInputOpen = false
      } catch (error) {
        console.error('Failed to add note:', error)
        alert(error.message || 'Failed to add note. Please try again.')
      } finally {
        this.noteSaving = false
      }
    },
    removeTimelineEntry(entryId) {
      this.notes = this.notes.filter(n => n.id !== entryId)
    },

    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    // The "Go to" dropdown — linked records on request. A linkless thread has
    // nothing to go to, so opening it goes straight into the picker.
    toggleGoTo() {
      if (this.goToOpen) { this.closeGoTo(); return }
      this.goToOpen = true
      this.replyMenuOpen = false
      if ((!this.entityLinks || this.entityLinks.length === 0) && !this.linkPickerOpen) this.toggleLinkPicker()
      // The panel hangs below the actions card, so it can land past the fold.
      // Bring it into view only when it isn't already there; scroll-mb-8 keeps
      // a little of the pane showing beneath it rather than parking it flush.
      this.$nextTick(() => {
        this.$refs.goToPanel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    },

    toggleReplyMenu() {
      this.replyMenuOpen = !this.replyMenuOpen
      if (this.replyMenuOpen) this.goToOpen = false
    },

    closeGoTo() {
      this.goToOpen = false
      this.replyMenuOpen = false
      this.managingLinks = false
      this.closeLinkPicker()
    },

    // Open the existing filing UI for a document that arrived by email, with
    // the document already attached and the extraction already filled in. The
    // three steps this replaces — download, find the record, upload — are the
    // whole point; the form itself is unchanged, and the user confirms every
    // field exactly as they do for a hand-upload.
    openFilingDoorway(doorway) {
      this.closeGoTo()
      if (doorway.kind === 'certificate') {
        // Exactly the detail shape the per-attachment chip already sends, so
        // the modal it lands in is unchanged.
        window.dispatchEvent(new CustomEvent('open-certificate-modal', {
          detail: {
            mode: 'create',
            property_id: doorway.property_id,
            property_address: doorway.property_label,
            property_pickable: true,
            document_address: doorway.analysis?.property_address,
            email_attachment: {
              id: doorway.email_attachment_id,
              filename: doorway.filename,
              content_type: doorway.content_type,
              ...doorway.analysis
            }
          }
        }))
      } else {
        // invoice_prefill / quote_prefill are the issue slideover's doorway
        // entrances, shared with Alice's cards. They open the modal filled
        // in, and the save takes an email_attachment_id and attaches the
        // document server-side — so the emailed PDF becomes the job's
        // document without a download. An invoice for a compliance renewal
        // opens the renewal in the same panel.
        const attachment = {
          id: doorway.email_attachment_id,
          filename: doorway.filename,
          content_type: doorway.content_type
        }
        const a = doorway.analysis || {}
        const detail = doorway.renewal_id ? { renewal_id: doorway.renewal_id } : { id: doorway.issue_id }

        if (doorway.kind === 'invoice') {
          // The FULL payable total goes in the amount field with no VAT split:
          // the agency passes a supplier's bill through, it does not account
          // for the supplier's VAT. The hand-upload path does the same thing —
          // InvoiceAnalyser returns the gross total as net_amount, vat 0.
          detail.invoice_prefill = {
            amount: a.total_amount != null ? Number(a.total_amount).toFixed(2) : '',
            date: a.issue_date,
            reference: a.reference,
            description: a.description,
            email_attachment: attachment
          }
        } else if (doorway.lines) {
          // A price typed into the email: its lines fill the form, and the
          // email is what the quote is linked back to
          detail.quote_prefill = {
            contractor_id: doorway.contractor_id,
            reference: doorway.reference,
            lines: doorway.lines.map(l => ({ description: l.description, quantity: 1, unit_price: l.amount })),
            email_thread_id: doorway.email_thread_id
          }
        } else {
          detail.quote_prefill = {
            contractor_id: doorway.contractor_id,
            contractor_name: a.contractor_company || a.contractor_name,
            reference: a.reference,
            email_attachment: attachment
          }
        }
        detail.invoice_prefill && (detail.invoice_prefill.contractor_id = doorway.contractor_id)
        this.$dispatch('open-issue-slideover', detail)
      }
    },

    toggleManageLinks() {
      this.managingLinks = !this.managingLinks
      if (this.managingLinks) this.closeLinkPicker()
    },

    // Remove a linked record. The server records this as a REJECTION, not a
    // delete — otherwise the next message sync re-derives it and the removal
    // silently undoes itself. Linking the same record again by hand revives it.
    async removeLink(type, id) {
      const key = `${type}_${id}`
      if (this.linkRemoving || !this.threadId) return
      this.linkRemoving = key
      try {
        const resp = await fetch(`/inbox/${this.threadId}/links/${type}/${id}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json'
          }
        })
        const data = await resp.json().catch(() => ({}))
        if (resp.ok && data.success) {
          this.entityLinks = data.entity_links
          if (data.thread) this.thread = { ...this.thread, ...data.thread }
          if (!this.entityLinks.length) this.managingLinks = false
        } else {
          this.error = data.error || 'Could not remove that link'
        }
      } catch (e) {
        console.error('Remove link failed:', e)
        this.error = 'Could not remove that link'
      } finally {
        this.linkRemoving = null
      }
    },


    // "Suggest a response" asks before it writes. The user holds facts the
    // records never will — what the landlord said on the phone, a decision
    // taken in the office — and that is exactly what decides the reply. It
    // is always asked and never remembered, but answering is optional: a
    // blank instruction has its own branch in the prompt, so either path
    // costs the same single call.
    toggleSuggest() {
      if (this.drafting || this.aiWorking) return
      if (this.suggestOpen) { this.suggestOpen = false; return }
      this.summarySections = null
      this.suggestInstruction = ''
      this.suggestOpen = true
      // The ask opens below the thread, which on anything but a one-message
      // thread is off the bottom of the pane — being asked a question you
      // cannot see is worse than not being asked. Scroll first, then focus,
      // or the focus itself yanks the pane somewhere else.
      this.scrollAiPanelIntoView('suggestPanel')
      this.$nextTick(() => this.$refs.suggestInput?.focus({ preventScroll: true }))
    },

    // How many records the answer is drawn from — shown on the ask so the user
    // knows it reads more than the emails, and on the summary so they know
    // what it read.
    linkedRecordCount() {
      return (this.entityLinks || []).length
    },

    // Who the reply is going to, for "…anything you want to say to Simon?".
    // Falls back to the neutral phrasing when the thread has no clear party.
    replyRecipientName() {
      const inbound = [...(this.messages || [])].reverse().find(m => m.direction === 'inbound')
      const name = (inbound?.from_name || '').trim()
      return name ? name.split(/\s+/)[0] : null
    },

    // Summarise — one call over the thread AND its linked records. Never
    // pre-computed: a summary is only true at the moment it is asked for.
    async summariseThread() {
      if (this.aiWorking || !this.threadId) return
      this.suggestOpen = false
      this.summarySections = null
      this.aiWorking = true
      this.aiWorkingLabel = `Reading the thread and ${this.linkedRecordCount()} linked records…`
      // Move to the skeleton straight away, so the wait happens in view.
      this.scrollAiPanelIntoView('aiWorkingPanel')
      try {
        const resp = await fetch(`/inbox/${this.threadId}/summarise`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })
        const data = await resp.json().catch(() => ({}))
        if (resp.ok && data.success) {
          this.summarySections = data.sections
          this.summaryMeta = { messages: data.message_count, records: data.record_count }
          // The skeleton and the summary are different heights, so re-anchor
          // once the real thing has rendered.
          this.scrollAiPanelIntoView('summaryPanel')
        } else {
          this.error = data.error || 'Could not summarise this thread'
        }
      } catch (e) {
        console.error('Summarise failed:', e)
        this.error = 'Could not summarise this thread'
      } finally {
        this.aiWorking = false
      }
    },

    closeSummary() {
      this.summarySections = null
    },

    // Bring an AI panel to the top of the pane so it fills the view. Answers
    // render below the thread and the actions bar, which on a long thread is
    // off-screen — asking for a summary and being left looking at the email
    // you asked about makes the feature feel like it did nothing. `start`
    // rather than `nearest`: the point is for the answer to fill the pane, not
    // to be technically visible at the bottom edge.
    scrollAiPanelIntoView(ref) {
      this.$nextTick(() => {
        this.$refs[ref]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    // Open the composer first so the draft has somewhere to land, then make
    // the one call. draftInstruction is the same field "Help me write" uses,
    // so refining afterwards is the identical round trip.
    async runSuggest() {
      if (this.drafting || this.aiWorking) return
      const instruction = this.suggestInstruction
      this.suggestOpen = false
      this.suggestInstruction = ''
      this.summarySections = null
      this.suggestions = null
      this.usedSteer = instruction
      this.aiWorking = true
      this.aiWorkingLabel = instruction
        ? 'Applying what you said across the thread…'
        : `Reading the thread and ${this.linkedRecordCount()} linked records…`
      this.scrollAiPanelIntoView('aiWorkingPanel')
      try {
        const resp = await fetch(`/inbox/${this.threadId}/suggest_replies`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ instruction })
        })
        const data = await resp.json().catch(() => ({}))
        if (resp.ok && data.success) {
          const suggested = data.suggestions || []
          // One reply is not a queue. When the thread calls for a single
          // message, that message IS the answer — it goes straight into the
          // composer, where it can be read and edited. A card announcing "1
          // reply suggested" and a "Review & send" button in front of it is
          // ceremony around a decision that was never offered. The card set
          // only earns its place when there is a choice of messages to make.
          if (suggested.length === 1) {
            this.aiWorking = false
            this.reviewSuggestion(suggested[0])
          } else {
            this.suggestions = suggested
            this.scrollAiPanelIntoView('suggestionsPanel')
          }
        } else {
          this.error = data.error || 'Could not prepare a reply'
        }
      } catch (e) {
        console.error('Suggest replies failed:', e)
        this.error = 'Could not prepare a reply'
      } finally {
        this.aiWorking = false
      }
    },

    // Open one suggestion in the composer. The card STAYS in the queue until
    // the message is actually sent or dismissed — opening a draft and then
    // discarding it must not silently lose the suggestion.
    reviewSuggestion(suggestion) {
      if (suggestion.reply) {
        // Answering the thread happens IN PLACE, exactly as clicking Reply
        // does — no second slideover for a message that belongs here. Reply
        // ALL, because the reply's recipients are the thread's own: whoever
        // wrote plus everyone copied.
        if (this.replyAllCcRecipients.length > 0) this.startReplyAll()
        else this.startReply()
        this.$nextTick(() => this.setReplyDraft(this._personaliseDraft(suggestion.body)))
      } else {
        // A message to someone NOT on the thread is a new email, not a reply.
        // openCompose() wipes the thread out of this component, so remember
        // where we came from: closing the compose must put the user back on
        // the thread they were reading, not on an empty inbox.
        this._returnToThreadId = this.threadId
        this.openComposePreFilled({
          to: suggestion.to,
          subject: suggestion.subject || suggestion.purpose,
          body_html: this._plainTextToHtml(this._personaliseDraft(suggestion.body))
        })
      }
      this._openSuggestionKey = this.suggestionKey(suggestion)
    },

    dismissSuggestion(suggestion) {
      const key = this.suggestionKey(suggestion)
      this.suggestions = (this.suggestions || []).filter(s => this.suggestionKey(s) !== key)
      if (!this.suggestions.length) this.suggestions = null
    },

    suggestionKey(suggestion) {
      return `${suggestion.purpose}::${(suggestion.to || []).map(t => t.email).join(',')}`
    },

    // The repairs still to raise, and the ones already raised — the panel is
    // a ledger, not a list. A two-repair email's second repair is the one
    // that gets forgotten, so a raised row stays visible pointing at its job
    // rather than vanishing and taking the count with it.
    liveJobCandidates() {
      return this.jobCandidates.filter(c => !c.raised_issue_id && !c.raised_job_id && !c.dismissed)
    },

    raisedJobCandidates() {
      return this.jobCandidates.filter(c => c.raised_issue_id || c.raised_job_id)
    },

    // The property a repair lands on. Candidates are thread-level (the
    // classifier reads text, not records), so a thread standing on exactly
    // one property needs no choice; on two, the row can't know, and the
    // generic per-property rows below are the honest way through.
    soleLinkedProperty() {
      const properties = this.entityLinks.filter(l => l.type === 'Property')
      return properties.length === 1 ? properties[0] : null
    },

    // The repair section owns job creation when it renders — it carries the
    // verb in its header and its own "Something else" row — so the generic
    // per-property row stands down rather than offering the same thing twice.
    showRepairSection() {
      return !!this.soleLinkedProperty() &&
        (this.liveJobCandidates().length > 0 || this.raisedJobCandidates().length > 0)
    },

    // Open the job form pre-filled from this thread. Nothing is created —
    // the human reviews and saves.
    //
    // With a candidate, the form arrives filled from the repair the
    // classifier read out of the thread. Without one, the click is the
    // generic "raise something here" row and the form arrives holding only
    // the property: an empty field a person fills in beats a full one they
    // have to empty first.
    async createMaintenanceJob(propertyId, candidate = null) {
      if (this.creatingJob) return
      this.creatingJob = candidate ? `candidate-${candidate.id}` : propertyId
      const query = new URLSearchParams({ property_id: propertyId })
      if (candidate) query.set('candidate_id', candidate.id)
      try {
        const resp = await fetch(`/inbox/${this.threadId}/job_prefill?${query}`, {
          headers: { 'Accept': 'application/json' }
        })
        if (!resp.ok) throw new Error(`job_prefill failed: ${resp.status}`)
        const prefill = await resp.json()
        this.closeGoTo()
        // The thread rides along so the issue that gets created is linked
        // back to the email that caused it, and the candidate marked raised
        this.$dispatch('open-new-issue-slideover', { ...prefill, reported_via: prefill.reported_by === 'landlord' ? 'landlord' : 'tenant', email_thread_id: this.threadId })
      } catch (e) {
        console.error('Create maintenance job failed:', e)
        // Still open the form on the property they picked — a form with the
        // address in it beats a dead button.
        this.closeGoTo()
        this.$dispatch('open-new-issue-slideover', { property_id: propertyId, email_thread_id: this.threadId })
      } finally {
        this.creatingJob = null
      }
    },

    // A repair raised from this thread — mark it in place and reopen the
    // panel, so the next repair is the thing you are looking at rather than
    // something you have to remember. Dispatched by the job slideover on a
    // successful create.
    onJobCandidateRaised(candidateId, issueId) {
      const candidate = this.jobCandidates.find(c => c.id === candidateId)
      if (!candidate) return
      candidate.raised_issue_id = issueId
      if (this.liveJobCandidates().length) this.goToOpen = true
    },

    toggleLinkPicker() {
      this.linkPickerOpen = !this.linkPickerOpen
      if (this.linkPickerOpen) {
        this.linkQuery = ''
        this.linkResults = []
        this.$nextTick(() => this.$refs.linkSearchInput?.focus())
      }
    },

    closeLinkPicker() {
      this.linkPickerOpen = false
      this.linkQuery = ''
      this.linkResults = []
    },

    // The × on the search box. On a thread with nothing linked the picker was
    // the whole panel, so closing it would leave an empty card — fold the
    // panel away with it. Anything else the panel can show keeps it open.
    dismissLinkPicker() {
      this.closeLinkPicker()
      if (!this.panelHasRows()) this.closeGoTo()
    },

    panelHasRows() {
      return Boolean(
        (this.entityLinks && this.entityLinks.length) ||
        (this.filingDoorways && this.filingDoorways.length) ||
        this.valuationDoorway ||
        this.liveJobCandidates().length
      )
    },

    // Debounced unified entity search for the manual-link picker.
    searchLinkEntities() {
      clearTimeout(this._linkSearchTimeout)
      const query = this.linkQuery.trim()
      if (query.length < 2) {
        this.linkResults = []
        this.linkSearching = false
        return
      }
      this.linkSearching = true
      this._linkSearchTimeout = setTimeout(async () => {
        try {
          const resp = await fetch(`/inbox/${this.threadId}/link_search?q=${encodeURIComponent(query)}`, {
            headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content, 'Accept': 'application/json' }
          })
          if (resp.ok) {
            const data = await resp.json()
            this.linkResults = data.results || []
          }
        } catch (e) {
          console.error('Link search failed:', e)
        } finally {
          this.linkSearching = false
        }
      }, 250)
    },

    // Link the chosen entity (cascading to its obvious parents server-side),
    // then refresh the linked-records panel and header chips from the response.
    async addLink(type, id) {
      if (this.linkAdding || !this.threadId) return
      this.linkAdding = true
      try {
        const resp = await fetch(`/inbox/${this.threadId}/links`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ linkable_type: type, linkable_id: id })
        })
        const data = await resp.json().catch(() => ({}))
        if (resp.ok && data.success) {
          this.entityLinks = data.entity_links
          if (data.thread) this.thread = { ...this.thread, ...data.thread }
          this.closeLinkPicker()
        } else {
          alert(data.error || 'Failed to link entity')
        }
      } catch (e) {
        console.error('Add link failed:', e)
        alert('Failed to link entity. Please try again.')
      } finally {
        this.linkAdding = false
      }
    },

    // The website's valuation form, read back on arrival (Inbox::ValuationLead):
    // one click opens the Add-valuation form already filled in. Nothing exists
    // until the human presses Add, and the thread rides along so the pipeline
    // row that gets created is linked back to the email that produced it.
    openValuationDoorway() {
      if (!this.valuationDoorway) return
      this.openAddValuation({ ...this.valuationDoorway.prefill, source: 'website' })
    },

    // "Create valuation" in the link picker — the general case: a landlord who
    // wrote to us in prose. There is nothing on the book to link TO, so the
    // verb is create, pre-filled from whoever wrote. On a website-form thread
    // it is the doorway by another door — and on any other website form
    // (a contact-form lead asking for a valuation) whoever wrote is the person
    // in the body, never the site's noreply envelope.
    async createValuationFromThread() {
      if (this.valuationDoorway) return this.openValuationDoorway()
      if (this.valuationPrefilling) return
      this.valuationPrefilling = true
      try {
        // The whole conversation, read for the person, the home and the ask
        // — the address is usually three messages down, in prose.
        const resp = await fetch(`/inbox/${this.threadId}/valuation_prefill`, {
          headers: { 'Accept': 'application/json' }
        })
        if (!resp.ok) throw new Error(`valuation_prefill failed: ${resp.status}`)
        this.openAddValuation(await resp.json())
      } catch (e) {
        console.error('Create valuation prefill failed:', e)
        // Still open the form with whoever wrote: a half-filled form beats none.
        if (this.websiteLead) return this.openAddValuation({ ...this.websiteLead.prefill, source: 'website' })
        const name = this.threadSenderName().split(/\s+/).filter(Boolean)
        this.openAddValuation({
          first_name: name[0] || '',
          last_name: name.slice(1).join(' '),
          email: this.firstInboundMessage()?.from_email || ''
        })
      } finally {
        this.valuationPrefilling = false
      }
    },

    openAddValuation(prefill) {
      this.closeLinkPicker()
      this.closeGoTo()
      window.dispatchEvent(new CustomEvent('open-add-valuation-slideover', {
        detail: { prefill: { ...prefill, email_thread_id: this.threadId } }
      }))
    },

    firstInboundMessage() {
      return (this.messages || []).find(m => m.direction === 'inbound') || null
    },

    threadSenderName() {
      const inbound = this.firstInboundMessage()
      return (inbound?.from_name || inbound?.from_email || '').trim()
    },

    // The sub-line under "Create valuation" in the picker: who the prospect is.
    valuationProspectLine() {
      if (this.valuationPrefilling) return 'Reading the thread…'
      const who = this.valuationDoorway?.name || this.websiteLead?.name || this.threadSenderName()
      return who ? `New landlord lead — ${who}` : 'New landlord lead from this email'
    },

    // The Add-valuation form saved against THIS thread: pull the new links
    // (valuation + landlord) into the panel and drop the doorway, without
    // reloading the messages. Other threads' saves are not ours to react to.
    async onValuationCreated(detail) {
      if (!this.open || !this.threadId) return
      if (Number(detail?.email_thread_id) !== Number(this.threadId)) return
      await this.refreshLinks()
    },

    async refreshLinks() {
      try {
        const resp = await fetch(`/inbox/${this.threadId}.json`, { headers: { 'Accept': 'application/json' } })
        if (!resp.ok) return
        const data = await resp.json()
        this.entityLinks = data.entity_links || []
        this.valuationDoorway = data.valuation_doorway || null
        this.websiteLead = data.website_lead || null
        if (data.thread) this.thread = { ...this.thread, ...data.thread }
      } catch (e) {
        console.error('Refresh links failed:', e)
      }
    },

    // Clicking the AI icon beside "Help me write…": fire the draft request.
    // Reply mode works with or without an instruction; compose mode needs
    // at least a typed instruction for the AI to have something to go on,
    // so we focus the input instead.
    onAiIconClick() {
      if (this.drafting) return
      if (this.composing && !this.draftInstruction.trim()) {
        this.$refs.draftInstructionInput?.focus()
        return
      }
      this.requestDraft()
    },

    async requestDraft() {
      if (this.drafting) return
      if (this.composing && !this.draftInstruction.trim()) return
      if (!this.composing && !this.threadId) return
      this.drafting = true
      try {
        let url, payload
        if (this.composing) {
          url = '/inbox/draft_compose'
          payload = {
            instruction: this.draftInstruction,
            to: this.composerTo,
            subject: this.composerSubject
          }
          if (this.selectedContacts.length > 0) {
            const c = this.selectedContacts[0]
            payload.contact = { name: c.name, type: c.type, email: c.email }
          }
          if (this.matchedProperties.length > 0) {
            const p = this.matchedProperties[0]
            payload.property = { display_name: p.display_name, full_address: p.full_address }
          }
          // Who each addressee is, what the email is about, and the draft so
          // far, so the words come out to the right person about the right thing
          payload.recipients = this.composerToRecipients.filter(r => r.kind !== 'group').map(r => ({ name: r.name, type: r.type, id: r.id, email: r.email }))
          if (this._composeAbout) payload.about = this._composeAbout
          payload.body = this._activeTrixEditor()?.getDocument().toString() || ''

        } else {
          url = `/inbox/${this.threadId}/draft_reply`
          payload = { instruction: this.draftInstruction }
        }
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        if (resp.ok) {
          const data = await resp.json()
          if (data.draft) {
            this.setReplyDraft(this._personaliseDraft(data.draft))
            this.draftInstruction = ''
          }
        }
      } catch (e) { console.error('Draft failed:', e) }
      this.drafting = false
    },

    // --- Alice proposal-mode composer (ProposedAction send verbs) ---
    // The composer edits Alice's draft; Send approves the proposal and the
    // executor sends. One send path, edits recorded server-side as diffs.
    editProposalInComposer(pa) {
      if (!(pa.subject_type === 'EmailThread' && pa.subject_id === this.threadId)) return
      if (pa.verb === 'send_email_reply') {
        this.composerMode = 'reply'
        this.composerToRecipients = String(pa.preview.to || '').split(',').filter(Boolean)
          .map(e => ({ kind: 'contact', email: e.trim(), name: e.trim(), type: null, id: null }))
        this.composerTo = ''
        this.composerCcRecipients = []
        this.composerCc = ''
        this.composerOpen = true
        this.closeGoTo()
        this._scrollComposerIntoView()
      } else {
        this.openCompose()
        this.composerSubject = pa.preview.subject || ''
        this.composerToRecipients = String(pa.preview.to || '').split(',').filter(Boolean)
          .map(e => ({ kind: 'contact', email: e.trim(), name: e.trim(), type: null, id: null }))
      }
      this.setReplyDraft(pa.preview.body_html || '')
      this._proposalMode = pa
      this._focusReplyEditor?.()
    },

    async sendProposalApproval() {
      const pa = this._proposalMode
      if (!pa || this.sending) return
      this.sending = true
      try {
        const edited = { body_html: this.replyDraft }
        if (this.composing && this.composerSubject.trim()) edited.subject = this.composerSubject
        const ccStr = this._serializeRecipientEmails(this.composerCcRecipients, this.composerCc)
        if (ccStr) edited.cc = ccStr

        const resp = await fetch(`/proposed_actions/${pa.id}/approve`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ edited_payload: edited })
        })
        const data = await resp.json().catch(() => ({}))
        const card = this.proposedActions.find(row => row.id === pa.id)
        if (data.data && card) Object.assign(card, data.data)
        if (resp.ok) {
          this._proposalMode = null
          await this.discardReply()
          this.fetchThread() // pull in the message the executor just created
        } else {
          this.error = data.error || 'Approval failed'
          if (resp.status === 409 && card) card.status = 'superseded'
        }
      } finally {
        this.sending = false
      }
    },

    // A card was actioned inline (not via the composer): refresh the
    // message list after an executed send so the sent reply appears.
    handleProposalDecided(pa) {
      if (pa.status === 'executed' && (pa.verb === 'send_email_reply' || pa.verb === 'send_new_email') &&
          pa.subject_type === 'EmailThread' && pa.subject_id === this.threadId) {
        this.fetchThread()
      }
    },

    // opts.scheduledFor — a Date to book the send for instead of sending now.
    async sendReply(opts = {}) {
      if (!this.canSend || this.sending) return
      if (!this.composing && !this.threadId) return
      const scheduledFor = opts.scheduledFor instanceof Date ? opts.scheduledFor.toISOString() : null

      // Group chips present → mass email path. Show the confirmation
      // modal first; the actual send happens in confirmMassEmailSend.
      if (this.hasGroupChips) {
        this.openSendConfirmation()
        return
      }

      // Proposal mode: the composer is only the EDITOR — approval executes
      // Alice's stored payload (with these edits) through the executor.
      if (this._proposalMode) {
        return this.sendProposalApproval()
      }

      this.sending = true
      this.schedulingSend = !!scheduledFor
      try {
        // Let any in-flight autosave finish first so currentDraftId is
        // accurate — the send passes it up for the server to delete, and
        // racing it could strand a just-created draft in the Drafts tab.
        clearTimeout(this._autosaveTimer)
        if (this._saveChain) await this._saveChain.catch(() => {})

        const isCompose = this.composing
        const url = isCompose ? '/inbox/compose' : `/inbox/${this.threadId}/send_reply`
        // Build To/Cc/Bcc strings from chip arrays + any typed text.
        // (Skip group-chip synthetic emails — they're handled by the
        // mass email path above and never reach this branch.)
        const toStr = this._serializeRecipientEmails(this.composerToRecipients, this.composerTo)
        const ccStr = this._serializeRecipientEmails(this.composerCcRecipients, this.composerCc)
        const bccStr = this._serializeRecipientEmails(this.composerBccRecipients, this.composerBcc)

        const payload = isCompose
          ? {
              to: toStr,
              subject: this.composerSubject,
              body: this.replyDraft,
              cc: ccStr || null,
              bcc: bccStr || null,
              attachments: await this.prepareAttachments(),
              draft_id: this.currentDraftId || null,
              proposed_action_id: this._composeProposedActionId || null,
              scheduled_for: scheduledFor
            }
          : {
              body: this.replyDraft,
              mode: this.composerMode,
              to: toStr,
              cc: ccStr || null,
              bcc: bccStr || null,
              attachments: await this.prepareAttachments(),
              archive_after_send: this.archiveAfterSend || null,
              draft_id: this.currentDraftId || null,
              scheduled_for: scheduledFor
            }

        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        const data = await resp.json()
        if (resp.ok && data.success) {
          this._resetDraftTracking()
          if (data.status === 'scheduled') {
            window.dispatchEvent(new CustomEvent('scheduled-changed', { detail: { delta: 1 } }))
          }
          // A suggestion is only spent once its email has actually gone. It
          // survives opening and discarding — losing the suggestion because
          // you looked at the draft and changed your mind would be the wrong
          // way round.
          if (this._openSuggestionKey) {
            this.suggestions = (this.suggestions || []).filter(s => this.suggestionKey(s) !== this._openSuggestionKey)
            if (!this.suggestions.length) this.suggestions = null
            this._openSuggestionKey = null
          }
          if (this.currentDraftId) {
            const deletedDraftId = this.currentDraftId
            this.currentDraftId = null
            window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: -1, deletedDraftId } }))
          }
          if (isCompose) {
            // Notify the originating record (e.g. quote → mark landlord
            // email sent on the JobInvitation) so the model can record state
            // changes. ActionCable broadcasts from the affected models patch
            // the slideover in place. We also dispatch the global `job-saved`
            // custom event with the refreshed payload so the jobs index row
            // patches immediately without waiting on the broadcast.
            if (this._linkedSendCompletion?.url) {
              try {
                const completionResp = await fetch(this._linkedSendCompletion.url, {
                  method: 'POST',
                  headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    ...(this._linkedSendCompletion.payload || {}),
                    thread_id: data.thread_id
                  })
                })
                if (completionResp.ok) {
                  const completionData = await completionResp.json().catch(() => null)
                  if (completionData?.job) {
                    window.dispatchEvent(new CustomEvent('job-saved', {
                      detail: { id: completionData.job.id, data: completionData.job, isNew: false }
                    }))
                  }
                  // Generic notification so the originating page can patch
                  // its own state (e.g. an inspection row moving to Sent).
                  if (completionData) {
                    window.dispatchEvent(new CustomEvent('linked-send-completed', { detail: completionData }))
                  }
                }
              } catch (e) {
                console.warn('Linked send completion callback failed:', e)
              }
            }
            // New email sent — close the compose slideover
            this._forceClose()
          } else {
            // Reply/forward — insert the sent message
            const msg = data.sent_message
            const iframeId = 'email-' + msg.id
            this.messages.push({
              ...msg,
              _key: 'msg_' + msg.id,
              _iframeId: iframeId,
              _srcdoc: this.buildEmailSrcdoc(msg.body_trimmed || '', iframeId),
              _srcdocFull: this.buildEmailSrcdoc(msg.body_full || msg.body_trimmed || '', iframeId)
            })
            this.setReplyDraft('')
            this.attachedFiles = []
            this.composerMode = 'reply'
            this.composerOpen = false
            this.composerTo = ''
            this.composerToRecipients = []
            this.composerCc = ''
            this.composerCcRecipients = []
            this.composerBcc = ''
            this.composerBccRecipients = []
            this.composerSubject = ''
            this.showCcBcc = false
            this.userEditedDraft = false

            // Update thread message count
            if (this.thread) this.thread.message_count = this.messages.length

            // Scroll to bottom to show sent message
            this.$nextTick(() => {
              const container = this.$refs.messagesContainer
              if (container) container.scrollTop = container.scrollHeight
            })

            // If archived after send, close slideover and remove from inbox
            if (data.archived) {
              this._forceClose()
              window.dispatchEvent(new CustomEvent('remove-email-thread', { detail: { id: this.threadId } }))
            }
          }
        } else {
          alert(data.error || 'Failed to send reply')
        }
      } catch (e) {
        console.error('Send failed:', e)
        alert('Failed to send reply. Please try again.')
      }
      this.sending = false
      this.schedulingSend = false
    },

    async handleFileSelect(event) {
      const files = Array.from(event.target.files)
      event.target.value = '' // Reset so same file can be re-selected

      for (const file of files) {
        // Add file with uploading state — the template reads .name, .size
        const entry = { name: file.name, size: file.size, file, uploading: true, progress: 0, signed_blob_id: null, error: null }
        this.attachedFiles = [...this.attachedFiles, entry]
        const idx = this.attachedFiles.length - 1

        try {
          const manager = new window.DirectUploadManager({
            onProgress: (_f, pct) => {
              this.attachedFiles[idx].progress = Math.round(pct)
            }
          })
          const signedId = await manager.uploadFile(file)
          this.attachedFiles[idx].signed_blob_id = signedId
          this.attachedFiles[idx].uploading = false
          this.touchDraft() // persist the blob id so the draft keeps the file
        } catch (e) {
          // DirectUpload to S3 failed (CORS blip, offline, etc.). Fall back
          // to a server-side upload — Rails streams the bytes into the same
          // private S3 bucket and hands back a signed_blob_id so the send
          // path stays identical. No base64 round-trip through Ruby memory.
          console.warn('DirectUpload failed, using server-side fallback:', e)
          try {
            const signedId = await this.uploadAttachmentFallback(file)
            this.attachedFiles[idx].signed_blob_id = signedId
            this.attachedFiles[idx].uploading = false
            this.touchDraft()
          } catch (fallbackError) {
            console.error('Server-side fallback upload also failed:', fallbackError)
            this.attachedFiles[idx].uploading = false
            this.attachedFiles[idx].error = true
          }
        }
      }
    },

    // Trix fires trix-attachment-add when an image (or any file) is dragged
    // or pasted into the editor. We upload the bytes to our inline-image
    // endpoint, then call setAttributes so Trix swaps the temporary blob:
    // URL for a permanent server URL — this also clears the progress bar
    // overlay that would otherwise sit on top of the image forever.
    async uploadInlineImage(event) {
      const attachment = event.attachment
      const file = attachment?.file
      if (!file) return

      // Only handle images inline. Anything else (PDFs, etc.) is removed
      // from the editor — those should go through the paperclip button so
      // they're sent as real attachments.
      if (!file.type.startsWith('image/')) {
        attachment.remove()
        return
      }

      try {
        const formData = new FormData()
        formData.append('file', file)

        const xhr = new XMLHttpRequest()
        xhr.open('POST', '/inbox/inline_images', true)
        xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]')?.content)
        xhr.setRequestHeader('Accept', 'application/json')

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            attachment.setUploadProgress((e.loaded / e.total) * 100)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText)
            // url tells Trix where to load the image from; href makes the
            // figure clickable. Setting both clears the progress overlay.
            attachment.setAttributes({
              url: data.url,
              href: data.url
            })
          } else {
            console.error('Inline image upload failed:', xhr.status, xhr.responseText)
            attachment.remove()
          }
        })

        xhr.addEventListener('error', () => {
          console.error('Inline image upload network error')
          attachment.remove()
        })

        xhr.send(formData)
      } catch (e) {
        console.error('Inline image upload exception:', e)
        attachment.remove()
      }
    },

    removeAttachment(idx) {
      this.attachedFiles.splice(idx, 1)
      this.touchDraft()
    },

    // Open an attached file in a new tab so the agent can verify the contents
    // before sending. Two cases: (1) freshly-selected local files have a
    // File object — use a blob URL; (2) pre-uploaded blobs (e.g. quote PDFs
    // attached by the composer flow) only carry signed_blob_id — open via
    // ActiveStorage's signed redirect, which works for both public and
    // private services.
    openAttachmentPreview(file) {
      if (!file || file.uploading) return

      let url
      let revoke = false
      if (file.file instanceof File) {
        url = URL.createObjectURL(file.file)
        revoke = true
      } else if (file.signed_blob_id) {
        const filename = encodeURIComponent(file.name || 'attachment')
        url = `/rails/active_storage/blobs/redirect/${file.signed_blob_id}/${filename}`
      } else {
        return
      }

      const win = window.open(url, '_blank', 'noopener')
      if (!win) {
        if (revoke) URL.revokeObjectURL(url)
        return
      }
      // Free the blob URL once the new tab has had time to load it. 60s is
      // plenty; revoking sooner would break the new tab.
      if (revoke) setTimeout(() => URL.revokeObjectURL(url), 60_000)
    },

    resizeTextarea(el, minHeight = 80) {
      if (!el) return
      // Cap height to prevent unbounded growth on mobile (avoids layout
      // jumping when the virtual keyboard is active and the browser
      // continuously tries to scroll the focused element into view).
      const maxHeight = Math.min(window.innerHeight * 0.4, 300)
      el.style.height = 'auto'
      const desired = Math.max(minHeight, el.scrollHeight)
      el.style.height = Math.min(desired, maxHeight) + 'px'
      el.style.overflowY = desired > maxHeight ? 'auto' : 'hidden'
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    // Synchronous so draft payloads can be captured at call time; the send
    // paths still `await` it, which is a harmless no-op on a plain value.
    prepareAttachments() {
      if (this.attachedFiles.length === 0) return []

      // Every successful upload — DirectUpload or server-side fallback —
      // produces a signed_blob_id. The server rejects attachments without
      // one, so we filter here too. Failed uploads (`entry.error === true`)
      // should have been surfaced to the user already.
      return this.attachedFiles
        .filter(entry => entry.signed_blob_id)
        .map(entry => ({
          filename: entry.name,
          content_type: entry.content_type || entry.file?.type || 'application/octet-stream',
          signed_blob_id: entry.signed_blob_id
        }))
    },

    // Server-side upload fallback — used only when DirectUpload fails.
    // Streams the File as multipart/form-data to /inbox/upload_attachment
    // which puts the bytes on S3 and returns a signed_blob_id.
    async uploadAttachmentFallback(file) {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/inbox/upload_attachment', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        },
        body: formData
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || `Upload failed (${response.status})`)
      }

      const data = await response.json()
      return data.signed_blob_id
    },

    async fetchThread() {
      this.loading = true
      try {
        const response = await fetch(`/inbox/${this.threadId}.json`, {
          headers: { 'Accept': 'application/json' }
        })

        if (!response.ok) throw new Error('Failed to load thread')

        const data = await response.json()
        this.thread = data.thread
        // Pre-compute srcdoc for each message so the value is stable across
        // Alpine re-evaluations (buildEmailSrcdoc generates a new nonce each
        // call, which would cause iframe reloads if called reactively).
        this.messages = data.messages.map(msg => {
          const iframeId = 'email-' + msg.id
          return {
            ...msg,
            _key: 'msg_' + msg.id,
            _iframeId: iframeId,
            _srcdoc: this.buildEmailSrcdoc(msg.body_trimmed || '(no content)', iframeId),
            _srcdocFull: this.buildEmailSrcdoc(msg.body_full || msg.body_trimmed || '(no content)', iframeId)
          }
        })
        this.entityLinks = data.entity_links
        this.workItems = data.work_items || []
        this.jobCandidates = data.job_candidates || []
        this.filingDoorways = data.filing_doorways || []
        this.valuationDoorway = data.valuation_doorway || null
        this.websiteLead = data.website_lead || null
        this.proposedActions = data.proposed_actions || []
        this.notes = (data.notes || []).map(n => ({ ...n, _key: 'note_' + n.id, _isNote: true }))
        if (this._pendingDraft) {
          this._applyPendingDraft()
        }
        this.loading = false
        this.open = true

        // Scroll to bottom after render
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer
          if (container) container.scrollTop = container.scrollHeight
        })
      } catch (e) {
        console.error('Failed to load email thread:', e)
        this.error = 'Failed to load email thread'
        this.loading = false
      }
    },

    // Format date for message header (like Gmail: "23 Mar at 12:40 (2 days ago)")
    formatMessageDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

      let relative
      if (diffMins < 1) relative = 'just now'
      else if (diffMins < 60) relative = `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
      else if (diffHours < 24) relative = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
      else if (diffDays < 30) relative = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
      else relative = null

      const dateStr = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      })

      if (date.toDateString() === now.toDateString()) {
        return relative ? `${timeStr} (${relative})` : timeStr
      }

      const full = `${dateStr} at ${timeStr}`
      return relative ? `${full} (${relative})` : full
    },

    // Format date for thread header
    formatThreadDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },

    // Get initials for avatar
    getInitials(name, email) {
      if (name) {
        const cleaned = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
        const parts = cleaned.trim().split(/\s+/).filter(Boolean)
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        if (parts.length === 1) return parts[0][0].toUpperCase()
      }
      if (email) return email[0].toUpperCase()
      return '?'
    },

    // Get avatar background color based on direction
    getAvatarClass(direction) {
      return direction === 'outbound'
        ? 'bg-blue-500 text-white'
        : 'bg-gray-200 text-gray-600'
    },

    // Get entity chip color class
    getChipClass(type) {
      const classes = {
        Property: 'bg-[#EFF6FF] text-[#2563EB]',
        Landlord: 'bg-[#F5F3FF] text-[#7C3AED]',
        Contractor: 'bg-[#FFFBEB] text-[#D97706]',
        Person: 'bg-[#ECFDF5] text-[#059669]',
        Tenancy: 'bg-[#F0FDFA] text-[#0D9488]',
        Job: 'bg-[#FFF7ED] text-[#EA580C]',
        LegacyJob: 'bg-[#FFF7ED] text-[#EA580C]'
      }
      return classes[type] || 'bg-[#F0F2F5] text-[#6B7280]'
    },

    // File type icon based on content_type
    getFileIcon(contentType) {
      if (!contentType) return 'ph-file'
      if (contentType === 'application/pdf') return 'ph-file-pdf'
      if (contentType.startsWith('image/')) return 'ph-file-image'
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return 'ph-file-xls'
      if (contentType.includes('word') || contentType.includes('document')) return 'ph-file-doc'
      if (contentType.includes('presentation') || contentType.includes('powerpoint')) return 'ph-file-ppt'
      if (contentType.includes('zip') || contentType.includes('compressed') || contentType.includes('archive')) return 'ph-file-zip'
      if (contentType.startsWith('text/')) return 'ph-file-text'
      if (contentType.startsWith('video/')) return 'ph-file-video'
      if (contentType.startsWith('audio/')) return 'ph-file-audio'
      return 'ph-file'
    },

    // File type icon color
    getFileIconColor(contentType) {
      if (!contentType) return 'text-gray-400'
      if (contentType === 'application/pdf') return 'text-red-500'
      if (contentType.startsWith('image/')) return 'text-blue-500'
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return 'text-green-600'
      if (contentType.includes('word') || contentType.includes('document')) return 'text-blue-600'
      if (contentType.includes('zip') || contentType.includes('compressed')) return 'text-yellow-600'
      return 'text-gray-400'
    },

    // File type background color for icon block
    getFileIconBg(contentType) {
      if (!contentType) return 'bg-gray-400'
      if (contentType === 'application/pdf') return 'bg-red-500'
      if (contentType.startsWith('image/')) return 'bg-blue-500'
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return 'bg-green-600'
      if (contentType.includes('word') || contentType.includes('document')) return 'bg-blue-600'
      if (contentType.includes('presentation') || contentType.includes('powerpoint')) return 'bg-orange-500'
      if (contentType.includes('zip') || contentType.includes('compressed')) return 'bg-yellow-600'
      if (contentType.startsWith('text/')) return 'bg-gray-500'
      if (contentType.startsWith('video/')) return 'bg-purple-500'
      if (contentType.startsWith('audio/')) return 'bg-pink-500'
      return 'bg-gray-400'
    },

    // Accent color for file type card
    getFileAccent(contentType) {
      if (!contentType) return '#9ca3af'
      if (contentType === 'application/pdf') return '#e04a3f'
      if (contentType.startsWith('image/')) return '#3b82f6'
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return '#16a34a'
      if (contentType.includes('word') || contentType.includes('document')) return '#2563eb'
      if (contentType.includes('presentation') || contentType.includes('powerpoint')) return '#ea580c'
      if (contentType.includes('zip') || contentType.includes('compressed')) return '#ca8a04'
      if (contentType.startsWith('text/')) return '#6b7280'
      if (contentType.startsWith('video/')) return '#7c3aed'
      if (contentType.startsWith('audio/')) return '#db2777'
      return '#9ca3af'
    },

    // Darker fold color for file type badge
    getFileFold(contentType) {
      if (!contentType) return '#6b7280'
      if (contentType === 'application/pdf') return '#c43a30'
      if (contentType.startsWith('image/')) return '#2563eb'
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return '#15803d'
      if (contentType.includes('word') || contentType.includes('document')) return '#1d4ed8'
      if (contentType.includes('presentation') || contentType.includes('powerpoint')) return '#c2410c'
      if (contentType.includes('zip') || contentType.includes('compressed')) return '#a16207'
      if (contentType.startsWith('text/')) return '#4b5563'
      if (contentType.startsWith('video/')) return '#6d28d9'
      if (contentType.startsWith('audio/')) return '#be185d'
      return '#6b7280'
    },

    // Short label for file type badge (e.g. "PDF", "IMG", "DOC")
    getFileLabel(contentType) {
      if (!contentType) return 'FILE'
      if (contentType === 'application/pdf') return 'PDF'
      if (contentType.startsWith('image/')) return 'IMG'
      if (contentType.includes('spreadsheet') || contentType.includes('excel')) return 'XLS'
      if (contentType === 'text/csv') return 'CSV'
      if (contentType.includes('word') || contentType.includes('document')) return 'DOC'
      if (contentType.includes('presentation') || contentType.includes('powerpoint')) return 'PPT'
      if (contentType.includes('zip') || contentType.includes('compressed')) return 'ZIP'
      if (contentType.startsWith('text/')) return 'TXT'
      if (contentType.startsWith('video/')) return 'VID'
      if (contentType.startsWith('audio/')) return 'MP3'
      return 'FILE'
    },

    // Format file size
    formatSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    // Wrap email HTML in a minimal document for iframe srcdoc rendering.
    // Strips elements that break rendering in an iframe context (like Gmail does):
    //   - <link> external stylesheets (can override host styles or hide content)
    //   - <base> tags (conflicts with our own target="_blank")
    //   - <script> tags (sandbox blocks them, but strip to avoid console noise)
    //   - @font-face rules (font files resolve to wrong URLs and 404)
    //   - Tracking pixels (invisible 1x1 images that trigger slow redirect chains)
    //   - Structural tags (<html>, <head>, <body>, <title>, <!DOCTYPE>) that conflict
    //     with the srcdoc wrapper and can break the height-reporting script
    _personaliseDraft(text) {
      if (!text) return text
      const firstName = this.$root.dataset.userFirstName
      if (firstName) {
        return text.replace(/\[SENDER_NAME\]/g, firstName)
      }
      return text
    },

    // Set replyDraft (HTML body) and sync into the active Trix editor.
    // Use this whenever replyDraft is updated programmatically (resets, AI drafts,
    // ActionCable updates) — Trix only listens to its own input on initialize, so
    // it needs an explicit loadHTML to pick up programmatic changes.
    //
    // Accepts HTML or plain text. Plain text is converted to HTML so newlines
    // and paragraph breaks are preserved when Trix loads it (the AI drafting
    // endpoints return plain text).
    setReplyDraft(input) {
      const looksLikeHtml = /<[a-z\/]/i.test(input || '')
      const html = (input && !looksLikeHtml) ? this._plainTextToHtml(input) : (input || '')
      this.replyDraft = html
      this._suppressUserEdited = true
      this.$nextTick(() => {
        const editor = this.composing ? this.$refs.composeTrixEditor : this.$refs.replyTrixEditor
        if (editor && editor.editor) {
          editor.editor.loadHTML(this.replyDraft)
        }
        // Allow trix-change from loadHTML to settle before re-enabling
        this.$nextTick(() => { this._suppressUserEdited = false })
      })
    },

    _plainTextToHtml(text) {
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // Wrap everything in a single Trix block (<div>) to avoid the
      // per-block `<br><br>` terminators Trix adds when it parses adjacent
      // divs. Paragraph breaks (blank line in source) become `<br><br>`.
      // Single newlines (e.g. between "Kind regards," and the signer name)
      // stay as `<br>`. Trailing `<br><br><br>` gives breathing room before
      // the signature preview.
      const body = escaped
        .split(/\n{2,}/)
        .map(para => para.split('\n').join('<br>'))
        .join('<br><br>')
      return body.length ? `<div>${body}<br><br><br></div>` : ''
    },

    buildEmailSrcdoc(html, iframeId) {
      const isHtml = html.includes('<')
      let body
      if (isHtml) {
        body = html
          .replace(/<!DOCTYPE[^>]*>/gi, '')
          .replace(/<\/?(html|head|body|title|meta)\b[^>]*>/gi, '')
          .replace(/<link\b[^>]*>/gi, '')
          .replace(/<base\b[^>]*>/gi, '')
          .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
          .replace(/@font-face\s*\{[^}]*\}/gi, '')
          .replace(/<img\b[^>]*(?:width\s*=\s*["']?1["']?\b|height\s*=\s*["']?1["']?\b)[^>]*>/gi, '')
          .replace(/<img\b/gi, '<img loading="lazy"')
      } else {
        body = `<pre style="white-space:pre-wrap;margin:0;font-family:inherit">${html.replace(/&/g,'&amp;').replace(/</g,'&lt;')}</pre>`
      }
      // No CSP meta tag — the sandbox attribute on the iframe provides security
      // isolation (unique origin, no parent access). Script tags are already
      // stripped from email HTML above. A CSP nonce inside srcdoc causes race
      // conditions in Chrome where the script is blocked before the nonce is parsed.
      return `<!DOCTYPE html><html><head>
<base target="_blank"><style>
body{margin:0;display:flow-root;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:13px;color:#374151;line-height:1.625;word-break:break-word}
img{max-width:100%;height:auto}
a{color:#1a73e8;text-decoration:none}a:hover{text-decoration:underline}
table{max-width:100%}
blockquote{border-left:3px solid #d1d5db;padding-left:1em;color:#6b7280}
</style></head><body>${body}<script>
(function(){
  var lastH=0;var iframeId='${iframeId}';
  function postHeight(){
    var h=document.body.scrollHeight;
    if(h>0&&h!==lastH){lastH=h;window.parent.postMessage({type:'email-iframe-resize',height:h,id:iframeId},'*')}
  }
  // Fix squished images: when max-width:100% constrains width below the
  // explicit attribute, force height:auto so aspect ratio is preserved.
  // Images that fit within the container keep their explicit dimensions.
  function fixImg(img){
    var specW=img.getAttribute('width');
    if(specW&&img.offsetWidth<parseInt(specW)){img.style.height='auto'}
    postHeight();
  }
  document.querySelectorAll('img').forEach(function(img){
    if(img.complete){fixImg(img)}
    else{img.addEventListener('load',function(){fixImg(img)});img.addEventListener('error',postHeight)}
  });
  if(document.fonts&&document.fonts.ready){document.fonts.ready.then(postHeight)}
  postHeight();
  requestAnimationFrame(postHeight);
  setTimeout(postHeight,100);
  setTimeout(postHeight,500);
  setTimeout(postHeight,1500);

  // Auto-link bare email addresses and URLs in text nodes (like Gmail does).
  // Wrapped in try/catch so a failure here can never break height reporting.
  try{
    var emailRe=/\\b([a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,})\\b/g;
    var urlRe=/\\b(https?:\\/\\/[^\\s<>"']+)/gi;
    var walk=document.createTreeWalker(document.body,NodeFilter.SHOW_TEXT,null);
    var texts=[];
    while(walk.nextNode())texts.push(walk.currentNode);
    texts.forEach(function(node){
      var p=node.parentNode;
      if(!p||!p.closest||p.closest('a,script,style,code,pre'))return;
      var t=node.textContent;
      if(!emailRe.test(t)&&!urlRe.test(t))return;
      emailRe.lastIndex=0;urlRe.lastIndex=0;
      var safe=t.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      var linked=safe.replace(emailRe,'<a href="mailto:$1">$1</a>').replace(urlRe,'<a href="$1">$1</a>');
      if(linked!==safe){
        var span=document.createElement('span');
        span.innerHTML=linked;
        p.replaceChild(span,node);
      }
    });
    postHeight();
  }catch(e){}

  // Scroll the parent slideover when selecting text near the viewport edges.
  // The iframe is resized to its full content height, so we use screenY
  // (absolute screen position) and let the parent compare against the
  // scroll container's bounding rect.
  var selecting=false;
  document.addEventListener('mousedown',function(){selecting=true});
  document.addEventListener('mouseup',function(){
    selecting=false;
    window.parent.postMessage({type:'email-iframe-scroll',direction:0},'*');
  });
  document.addEventListener('mousemove',function(ev){
    if(!selecting)return;
    window.parent.postMessage({type:'email-iframe-scroll',screenY:ev.screenY},'*');
  });
})();
</script></body></html>`
    },

    // Get the last sender name for reply bar
    get lastSenderName() {
      if (!this.messages.length) return ''
      const lastInbound = this._lastInboundMessage()
      if (lastInbound) {
        const [to] = this._replyAddressesFor(lastInbound)
        return to ? (to.name || to.email) : ''
      }
      return this.messages[this.messages.length - 1].from_name || this.messages[this.messages.length - 1].from_email
    },

    get lastSenderEmail() {
      if (!this.messages.length) return ''
      const lastInbound = [...this.messages].reverse().find(m => m.direction === 'inbound')
      if (lastInbound) return lastInbound.from_email
      return this.messages[this.messages.length - 1].from_email
    },

    // Every email address that belongs to us on this thread, used to strip
    // ourselves from reply-to-all recipients. The connected mailbox address
    // (data-account-email) is authoritative and present even on a fresh
    // inbound thread with no outbound message yet; we also fold in the from
    // address of any message we've actually sent, to cover aliases.
    get ownEmails() {
      const emails = new Set()
      if (this.accountEmail) emails.add(this.accountEmail)
      for (const m of this.messages) {
        if (m.direction === 'outbound' && m.from_email) {
          emails.add(m.from_email.toLowerCase())
        }
      }
      return emails
    },

    // The agency's own mail domains. A shared inbox is reached by more than
    // one address — worthing@, admin@, a named member of staff — and mail
    // addressed to any of them is still addressed to us. Without this, an
    // ordinary two-party thread that happened to arrive at an alias looks
    // like it has a third party on it and offers "Reply to all".
    get ownDomains() {
      const domains = new Set()
      const add = (email) => {
        const domain = (email || '').toLowerCase().split('@')[1]
        if (domain) domains.add(domain)
      }
      add(this.accountEmail)
      for (const m of this.messages) {
        if (m.direction === 'outbound') add(m.from_email)
      }
      return domains
    },

    get replyVerbLabel() {
      return { reply: 'Reply', reply_all: 'Reply to all', forward: 'Forward' }[this.composerVerb] || 'Reply'
    },

    get replyVerbIcon() {
      return {
        reply: 'ph-arrow-bend-up-left',
        reply_all: 'ph-arrow-bend-double-up-left',
        forward: 'ph-arrow-bend-up-right'
      }[this.composerVerb] || 'ph-arrow-bend-up-left'
    },

    // The Go to panel's rows, grouped the way the matter reads: where it is,
    // who's in it, what it belongs to. A contact group holding more than one
    // reachable address gets a bulk Email action — emailing both tenants at
    // once is the common case, and doing it one row at a time makes two
    // separate threads out of one conversation.
    get goToGroups() {
      const links = this.entityLinks || []
      const of = (type) => links.filter(l => l.type === type)
      const plural = (rows, one, many) => (rows.length > 1 ? many : one)

      const contactGroup = (key, rows, one, many) => {
        const emails = rows.map(r => r.email).filter(Boolean)
        return {
          key,
          label: rows.length > 1 ? `${many} · ${rows.length}` : one,
          rows,
          emails,
          bulk: emails.length > 1 ? (emails.length === 2 ? 'Email both' : 'Email all') : null
        }
      }

      const groups = []
      const properties = of('Property')
      if (properties.length) {
        groups.push({ key: 'property', label: plural(properties, 'Property', 'Properties'), rows: properties })
      }
      // Label people by what they ARE, not by their table. Only when the whole
      // group shares one role — a group holding a tenant and an applicant has
      // no honest single heading, so it falls back.
      const people = of('Person')
      if (people.length) {
        const roles = [...new Set(people.map(p => p.role).filter(Boolean))]
        const uniform = roles.length === 1 && people.every(p => p.role === roles[0])
        const [one, many] = uniform ? [ roles[0], `${roles[0]}s` ] : [ 'Person', 'People' ]
        groups.push(contactGroup('person', people, one, many))
      }
      const landlords = of('Landlord')
      if (landlords.length) groups.push(contactGroup('landlord', landlords, 'Landlord', 'Landlords'))
      const contractors = of('Contractor')
      if (contractors.length) groups.push(contactGroup('contractor', contractors, 'Contractor', 'Contractors'))

      // Applications sit with tenancies and jobs: the same "what is happening
      // at this property" band, and an application is a tenancy that hasn't
      // started yet.
      const applications = of('TenancyApplication')
      const tenancies = of('Tenancy')
      // The work: an issue, or a compliance renewal. The server stopped
      // sending old Job links at the cutover; grouping only 'Job' left every
      // thread linked to an issue with no job row at all (thread 12075).
      const jobs = [...of('Issue'), ...of('Renewal')]
      if (applications.length) {
        groups.push({
          key: 'application',
          label: plural(applications, 'Application', 'Applications'),
          rows: applications
        })
      }
      if (tenancies.length || jobs.length) {
        const label = tenancies.length && jobs.length
          ? 'Tenancy & jobs'
          : (tenancies.length ? plural(tenancies, 'Tenancy', 'Tenancies') : plural(jobs, 'Job', 'Jobs'))
        groups.push({ key: 'tenancy_job', label, rows: [...tenancies, ...jobs] })
      }

      const valuations = of('Valuation')
      if (valuations.length) {
        groups.push({ key: 'valuation', label: plural(valuations, 'Valuation', 'Valuations'), rows: valuations })
      }
      return groups
    },

    // Bulk email a whole group — one composer addressed to everyone in it.
    emailGroup(group) {
      if (!group?.emails?.length) return
      this.closeGoTo()
      window.openEmail(group.emails, {})
    },

    // Recipients for "Reply to all" CC row — everyone on the last inbound
    // message's To/Cc, minus the sender (who becomes To) and ourselves.
    get replyAllCcRecipients() {
      if (!this.messages.length) return []
      const lastInbound = this._lastInboundMessage()
      if (!lastInbound) return []
      const replyEmails = this._replyAddressesFor(lastInbound).map(r => (r.email || '').toLowerCase())
      const everyone = [
        ...(lastInbound.to_recipients || []),
        ...(lastInbound.cc_recipients || [])
      ]
      const seen = new Set([(lastInbound.from_email || '').toLowerCase(), ...replyEmails, ...this.ownEmails].filter(Boolean))
      const ourDomains = this.ownDomains
      return everyone.filter(r => {
        const email = (r.email || r['email'] || '').toLowerCase()
        if (!email || seen.has(email)) return false
        if (ourDomains.has(email.split('@')[1])) return false
        seen.add(email)
        return true
      }).map(r => ({ email: r.email || r['email'], name: r.name || r['name'] || '' }))
    },

    get canSend() {
      // Block send while any attachment is still uploading to S3, or if
      // any failed to upload (both DirectUpload and the server-side fallback
      // returned an error). Sending now would drop the failed attachments
      // silently — better to force the user to remove or retry.
      if (this.attachedFiles.some(f => f.uploading || f.error)) return false
      const hasRecipients = this.composerToRecipients.length > 0 || this.composerTo.trim()
      if (this.composerMode === 'forward') return hasRecipients
      // Strip HTML tags before checking — Trix produces e.g. "<div><br></div>" for empty
      const bodyText = (this.replyDraft || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      if (!bodyText) return false
      if (this.composerMode === 'compose' && !hasRecipients) return false
      if (this.composerMode === 'compose' && !this.composerSubject.trim()) return false
      return true
    },

    get hasUnsavedContent() {
      const bodyText = (this.replyDraft || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      const hasSubject = (this.composerSubject || '').trim().length > 0
      const hasBody = bodyText.length > 0

      if (this.composing) {
        // New compose: any body or subject is worth keeping. Requiring a
        // recipient here silently discarded long emails written body-first;
        // recipients alone aren't kept — they're seconds to re-add.
        return hasBody || hasSubject
      }
      // Reply/forward: need body content that the user actually edited
      // (AI suggestions alone don't count as unsaved user content)
      return hasBody && this.userEditedDraft
    },

    // --- Draft persistence ---

    // trix-change handler for both composer copies. Programmatic loads
    // (AI drafts, reopened drafts) set _suppressUserEdited so they neither
    // claim user authorship nor trigger an autosave.
    onBodyChange(event) {
      this.replyDraft = event.target.value
      if (this._suppressUserEdited) return
      this.userEditedDraft = true
      this.touchDraft()
    },

    // Every user edit lands here: mark the draft dirty and (re)arm a short
    // debounce so the composer saves itself a moment after typing settles.
    // Programmatic content (AI suggestions, loaded drafts, thread wipes)
    // deliberately never touches — only real user edits create drafts.
    touchDraft() {
      this.draftDirty = true
      this._draftRevision++
      clearTimeout(this._autosaveTimer)
      this._autosaveTimer = setTimeout(() => this.autosaveDraft(), 2500)
    },

    autosaveDraft() {
      if (!this.draftDirty) return
      // Update an existing draft unconditionally; only *create* one once
      // there's content worth keeping.
      if (!this.currentDraftId && !this.hasUnsavedContent) return
      this._flushedRevision = this._draftRevision
      this.saveDraft({ interactive: false })
    },

    // Last-chance save before the composer state disappears: thread switch,
    // Turbo navigation, tab hide/close. The payload is captured synchronously
    // inside saveDraft(), so callers may wipe state immediately afterwards.
    // keepalive lets the request survive a real page teardown (64KB cap, so
    // it's only passed where the document actually dies).
    flushDraft({ keepalive = false } = {}) {
      clearTimeout(this._autosaveTimer)
      if (!this.draftDirty) return
      if (this._flushedRevision === this._draftRevision) return // save already queued
      if (!this.currentDraftId && !this.hasUnsavedContent) return
      this._flushedRevision = this._draftRevision
      this.saveDraft({ interactive: false, keepalive })
    },

    // The composer session is over (sent, discarded, or wiped for a new
    // email): stop any pending autosave and invalidate in-flight responses.
    _resetDraftTracking() {
      clearTimeout(this._autosaveTimer)
      this.draftDirty = false
      this._flushedRevision = -1
      this._draftEpoch++
    },

    // Captures the payload synchronously at call time (callers may wipe the
    // composer right after), then serialises the network sends on a promise
    // chain so overlapping saves can't race each other, drop the latest
    // text, or double-create a draft.
    saveDraft({ interactive = true, keepalive = false } = {}) {
      const payload = {
        draft_id: this.currentDraftId,
        thread_id: this.threadId,
        composer_mode: this.composing ? 'compose' : this.composerMode,
        subject: this.composerSubject,
        body: this.replyDraft,
        to_recipients: this.composerToRecipients,
        composer_to: this.composerTo,
        // The draft table only stores cc/bcc as plain strings; flatten
        // chip arrays back to "Name <email>" so they round-trip without
        // requiring a schema migration.
        cc: this._serializeRecipientLabels(this.composerCcRecipients, this.composerCc),
        bcc: this._serializeRecipientLabels(this.composerBccRecipients, this.composerBcc),
        show_cc_bcc: this.showCcBcc,
        attachments: this.prepareAttachments()
      }
      const revision = this._draftRevision
      const epoch = this._draftEpoch
      this._saveChain = (this._saveChain || Promise.resolve())
        .catch(() => {})
        .then(() => this._performDraftSave(payload, { revision, epoch, interactive, keepalive }))
      return this._saveChain
    },

    async _performDraftSave(payload, { revision, epoch, interactive, keepalive }) {
      // A save queued behind the one that created the draft won't have had
      // the id at capture time — patch it in so we update, not re-create.
      if (!payload.draft_id && epoch === this._draftEpoch && this.currentDraftId) {
        payload.draft_id = this.currentDraftId
      }
      this.savingDraft = true
      try {
        const resp = await fetch('/inbox/save_draft', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          keepalive,
          body: JSON.stringify(payload)
        })
        const data = await resp.json()
        if (data.success) {
          const isNew = !payload.draft_id
          if (epoch === this._draftEpoch) {
            this.currentDraftId = data.draft_id
            if (revision === this._draftRevision) this.draftDirty = false
          }
          window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: isNew ? 1 : 0 } }))
          return true
        }
        // Save was rejected (e.g. validation). For an explicit save, surface
        // it rather than letting the composer close and silently discard the
        // user's work; background autosaves stay dirty and retry on the next
        // keystroke or flush instead of alerting mid-typing.
        this._flushedRevision = -1
        if (interactive) alert(data.error || 'Could not save draft.')
        return false
      } catch (e) {
        console.error('Save draft failed:', e)
        this._flushedRevision = -1
        if (interactive) alert('Could not save draft. Please copy your text before closing.')
        return false
      } finally {
        this.savingDraft = false
      }
    },

    loadDraft(draft) {
      // Store pending draft data so fetchThread() can apply it after loading
      this._pendingDraft = draft

      if (draft.composer_mode === 'compose' || !draft.thread_id) {
        this.openCompose()
        this._applyPendingDraft()
      } else {
        // Reply/forward — open the thread; fetchThread() will call _applyPendingDraft()
        this.openThread(draft.thread_id)
      }
    },

    _applyPendingDraft() {
      const draft = this._pendingDraft
      if (!draft) return
      this._pendingDraft = null
      this.currentDraftId = draft.draft_id
      this.composerMode = draft.composer_mode || 'compose'
      this.composerOpen = true
      this.closeGoTo()
      this.composerSubject = (draft.subject === 'No subject' ? '' : draft.subject) || ''
      this.composerTo = draft.composer_to || ''
      this.composerToRecipients = draft.to_recipients || []
      // Cc/Bcc are persisted as plain strings; parse them back into chips
      // so the user sees the same UI they left, not raw "Name <email>" text.
      this.composerCcRecipients = this._parseEmailList(draft.cc)
      this.composerCc = ''
      this.composerBccRecipients = this._parseEmailList(draft.bcc)
      this.composerBcc = ''
      this.showCcBcc = draft.show_cc_bcc || false
      // Restore attachment chips from the draft's stored blobs. Each carries
      // its signed_blob_id so it round-trips through the next save and the
      // send path exactly like a freshly-uploaded file. Without this the
      // composer would reopen with the files silently gone (the bug that
      // let an email go out with no attachment).
      this.attachedFiles = Array.isArray(draft.attachments)
        ? draft.attachments.map(a => ({
            name: a.filename,
            size: a.size,
            file: null,
            uploading: false,
            progress: 100,
            signed_blob_id: a.signed_blob_id,
            error: null
          }))
        : []
      this.userEditedDraft = true
      this.$nextTick(() => this.setReplyDraft(draft.body || ''))
    },

    async guardSaveDraft() {
      await this.saveDraft()
      this.draftGuardOpen = false
      this._forceClose()
    },

    guardDiscard() {
      this._resetDraftTracking()
      if (this.currentDraftId) {
        const deletedDraftId = this.currentDraftId
        fetch(`/inbox/delete_draft?draft_id=${deletedDraftId}`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: -1, deletedDraftId } }))
      }
      this.draftGuardOpen = false
      this._forceClose()
    },

    // Discard the current draft and close the slideover
    async discardDraft() {
      this._resetDraftTracking()
      if (this.currentDraftId) {
        const deletedDraftId = this.currentDraftId
        await fetch(`/inbox/delete_draft?draft_id=${deletedDraftId}`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: -1, deletedDraftId } }))
      }
      this.currentDraftId = null
      this.setReplyDraft('')
      this.attachedFiles = []
      this.composerMode = 'reply'
      this.composerTo = ''
      this.composerToRecipients = []
      this.composerCc = ''
      this.composerCcRecipients = []
      this.composerBcc = ''
      this.composerBccRecipients = []
      this.composerSubject = ''
      this.showCcBcc = false
      this.userEditedDraft = false
      this._forceClose()
    },

    guardCancel() {
      this.draftGuardOpen = false
    },

    switchMode(mode) {
      if (mode === this.composerMode) return
      this.composerMode = mode

      this.composerTo = ''
      this.composerToRecipients = []
      this.setReplyDraft('')
      this.selectedContacts = []
      this.matchedProperties = []
      this._contactPropertyCache = {}
      this._subjectMatchedProperty = null
      this.showContactDropdown = false
    },

    // Reply/Reply-to-all/Forward triggers — shown when the composer is
    // collapsed. Each opens the composer with the appropriate mode and
    // pre-populated recipients. Thread-level triggers aim at the most
    // recent inbound message a person can answer (the counterpart in the
    // main conversation — never the bounce that followed our reply).
    // Per-message triggers (replyToMessage / replyAllToMessage) aim at
    // whichever message the user clicked — this matters after a forward,
    // where hitting Reply on msg 1 must go back to the tenant, not to
    // the forward recipient.
    startReply() {
      const target = this._lastInboundMessage() || this.messages[this.messages.length - 1]
      this.replyToMessage(target)
    },

    startReplyAll() {
      const target = this._lastInboundMessage() || this.messages[this.messages.length - 1]
      this.replyAllToMessage(target)
    },

    // Per-message Reply — populates composerToRecipients so the user can
    // see the target before typing a single word. For inbound messages
    // this is the sender; for outbound it's whoever we sent to (continues
    // a forward's sub-thread).
    replyToMessage(msg) {
      this.composerMode = 'reply'
      this.composerVerb = 'reply'
      this.composerToRecipients = this._replyTargetsFor(msg)
      this.composerTo = ''
      this.composerCc = ''
      this.composerCcRecipients = []
      this.composerBcc = ''
      this.composerBccRecipients = []
      this.showCcBcc = false
      this.composerOpen = true
      this.closeGoTo()
      this._scrollComposerIntoView()
      this._focusReplyEditor()
    },

    replyAllToMessage(msg) {
      this.composerMode = 'reply'
      this.composerVerb = 'reply_all'
      this.composerToRecipients = this._replyTargetsFor(msg)
      this.composerTo = ''
      const ccs = this.replyAllRecipientsFor(msg)
      this.composerCcRecipients = ccs.map(r => ({
        kind: 'contact', email: r.email, name: r.name || r.email, type: null, id: null
      }))
      this.composerCc = ''
      this.composerBccRecipients = []
      this.composerBcc = ''
      this.showCcBcc = ccs.length > 0
      this.composerOpen = true
      this.closeGoTo()
      this._scrollComposerIntoView()
      this._focusReplyEditor()
    },

    // Align the composer's top with the messages container's top. The
    // browser recomputes the target against live layout so this works
    // regardless of whether Trix has finished expanding its height.
    _scrollComposerIntoView() {
      this.$nextTick(() => {
        // Anchor on the actions bar, not the composer: the bar stays put while
        // you write, and scrolling the composer to the top would push it off
        // screen — taking Go to, Summary and Suggest a response with it.
        const target = this.$refs.actionsBar || this.$refs.composerContainer
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    _focusReplyEditor() {
      this.focusReplyOnInit = true
      this.$nextTick(() => {
        const el = this.$refs.replyTrixEditor
        if (el && el.editor && this.focusReplyOnInit) {
          el.focus()
          this.focusReplyOnInit = false
        }
      })
    },

    // For a given message, who should the "Reply" composer default to?
    // Inbound → its Reply-To, else the sender. Outbound → whoever we sent
    // it to (the forward recipient, or the last reply's To). Returned as a
    // chip array for composerToRecipients.
    _replyTargetsFor(msg) {
      if (!msg) return []
      if (msg.direction === 'inbound') {
        return this._replyAddressesFor(msg).map(r => ({
          kind: 'contact', email: r.email, name: r.name || r.email, type: null, id: null
        }))
      }
      return (msg.to_recipients || []).map(r => ({
        kind: 'contact',
        email: r.email,
        name: r.name || r.email,
        type: null,
        id: null
      }))
    },

    // Where a reply to an inbound message goes, as [{ email, name }]: its
    // Reply-To when it carried one, otherwise its sender. A website form
    // arrives from noreply@ with the enquirer in Reply-To — a reply to the
    // sender bounces. Mirrors EmailMessage#reply_recipients.
    _replyAddressesFor(msg) {
      const replyTo = (msg.reply_to_recipients || []).filter(r => r.email)
      if (replyTo.length) return replyTo
      return msg.from_email ? [{ email: msg.from_email, name: msg.from_name }] : []
    },

    // The inbound message a thread-level Reply answers: the latest one a
    // person can receive a reply to, skipping bounces and no-reply senders
    // (as EmailThread#counterparty_address does). Falls back to the latest
    // inbound when nothing on the thread is answerable.
    _lastInboundMessage() {
      const inbound = this.messages.filter(m => m.direction === 'inbound')
      const answerable = inbound.filter(m =>
        !NON_COUNTERPARTY_SENDERS.test(this._replyAddressesFor(m)[0]?.email || ''))
      return answerable[answerable.length - 1] || inbound[inbound.length - 1] || null
    },

    // Extra recipients for per-message Reply-all. Everyone on that
    // message's to+cc minus ourselves and the person who becomes the
    // primary To. Only meaningful for inbound messages — replying-all to
    // your own outbound is covered by the existing per-message Reply.
    replyAllRecipientsFor(msg) {
      if (!msg || msg.direction !== 'inbound') return []
      const replyEmails = this._replyAddressesFor(msg).map(r => (r.email || '').toLowerCase())
      const everyone = [
        ...(msg.to_recipients || []),
        ...(msg.cc_recipients || [])
      ]
      const seen = new Set([(msg.from_email || '').toLowerCase(), ...replyEmails, ...this.ownEmails].filter(Boolean))
      return everyone.filter(r => {
        const email = (r.email || '').toLowerCase()
        if (!email || seen.has(email)) return false
        seen.add(email)
        return true
      }).map(r => ({ email: r.email, name: r.name || '' }))
    },

    startForward() {
      this.composerMode = 'forward'
      this.composerVerb = 'forward'
      this.composerTo = ''
      this.composerToRecipients = []
      this.composerCc = ''
      this.composerCcRecipients = []
      this.composerBcc = ''
      this.composerBccRecipients = []
      this.showCcBcc = false
      this.composerOpen = true
      this.closeGoTo()
      this._scrollComposerIntoView()
    },

    // Discard the in-progress reply and collapse the composer back to the
    // Reply/Reply to all/Forward buttons. Distinct from `discardDraft` which
    // also closes the whole slideover.
    async discardReply() {
      this._resetDraftTracking()
      if (this.currentDraftId) {
        const deletedDraftId = this.currentDraftId
        try {
          await fetch(`/inbox/delete_draft?draft_id=${deletedDraftId}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
          })
          window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: -1, deletedDraftId } }))
        } catch (e) { console.error('Discard failed:', e) }
      }
      this.currentDraftId = null
      this.setReplyDraft('')
      this.attachedFiles = []
      this.composerMode = 'reply'
      this.composerTo = ''
      this.composerToRecipients = []
      this.composerCc = ''
      this.composerCcRecipients = []
      this.composerBcc = ''
      this.composerBccRecipients = []
      this.composerSubject = ''
      this.showCcBcc = false
      this.userEditedDraft = false
      this.composerOpen = false
      this._proposalMode = null
    },

    // Debounced typeahead trigger. `field` is one of 'to' | 'cc' | 'bcc' —
    // it tells the search/select pipeline which chip array to mutate when
    // the user picks a suggestion. Groups are only offered for the To field.
    onRecipientInput(field = 'to') {
      this.touchDraft() // the typed buffer is part of the draft payload
      this.activeRecipientField = field
      clearTimeout(this._contactSearchTimeout)
      const query = this._typedBufferFor(field).trim()
      if (query.length < 2) {
        this.contactResults = []
        this.groupResults = []
        this.showContactDropdown = false
        return
      }
      this._contactSearchTimeout = setTimeout(() => this.searchContacts(query, field), 200)
    },

    // Kept as a thin alias because the To input still binds to `onToInput()`
    // in views that haven't been touched and to keep older test selectors stable.
    onToInput() { this.onRecipientInput('to') },

    _typedBufferFor(field) {
      if (field === 'cc') return this.composerCc
      if (field === 'bcc') return this.composerBcc
      return this.composerTo
    },

    _clearTypedBufferFor(field) {
      if (field === 'cc') this.composerCc = ''
      else if (field === 'bcc') this.composerBcc = ''
      else this.composerTo = ''
    },

    _recipientsFor(field) {
      if (field === 'cc') return this.composerCcRecipients
      if (field === 'bcc') return this.composerBccRecipients
      return this.composerToRecipients
    },

    // Comma-separated bare emails for the to/cc/bcc wire payload. The
    // server's validator (URI::MailTo::EMAIL_REGEXP) rejects "Name <email>"
    // segments, so we never put display names on the wire. Group chips are
    // skipped — the mass-email path handles them separately.
    _serializeRecipientEmails(chips, typed) {
      const parts = chips
        .filter(r => r.kind !== 'group' && r.email)
        .map(r => r.email)
      const rest = (typed || '').trim()
      if (rest) parts.push(rest)
      return parts.join(', ')
    },

    // Comma-separated "Name <email>" form used only when persisting a draft,
    // so chips can be parsed back with their display names on reload.
    _serializeRecipientLabels(chips, typed) {
      const parts = chips
        .filter(r => r.kind !== 'group' && r.email)
        .map(r => (r.name && r.name !== r.email) ? `${r.name} <${r.email}>` : r.email)
      const rest = (typed || '').trim()
      if (rest) parts.push(rest)
      return parts.join(', ')
    },

    // Parse a comma-separated address string from a persisted draft back
    // into a chip array. Handles both "Name <email>" and bare-email forms.
    _parseEmailList(str) {
      if (!str || !String(str).trim()) return []
      return String(str).split(/[,;]/).map(s => s.trim()).filter(Boolean).map(part => {
        const m = part.match(/^(.+?)\s*<\s*(.+?)\s*>$/)
        if (m) return { kind: 'contact', email: m[2], name: m[1].trim() || m[2], type: null, id: null }
        return { kind: 'contact', email: part, name: part, type: null, id: null }
      })
    },

    async searchContacts(query, field = 'to') {
      this.searchingContacts = true
      const headers = {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
      try {
        const types = ['landlord', 'contractor', 'person'].map(t => `types[]=${t}`).join('&')
        // Only fetch groups for the To field — groups in cc/bcc would muddy
        // the mass-email send path (which keys off composerToRecipients).
        const requests = [
          fetch(`/api/people/search?q=${encodeURIComponent(query)}&${types}`, { headers })
        ]
        if (field === 'to') {
          requests.push(fetch(`/api/email_groups/search?q=${encodeURIComponent(query)}`, { headers }))
        }
        const [contactsResp, groupsResp] = await Promise.all(requests)

        if (contactsResp.ok) {
          const results = await contactsResp.json()
          // Only show contacts that have an email
          this.contactResults = results.filter(r => r.email).slice(0, 8)
        } else {
          this.contactResults = []
        }

        if (groupsResp && groupsResp.ok) {
          this.groupResults = await groupsResp.json()
        } else {
          this.groupResults = []
        }

        this.showContactDropdown = this.contactResults.length > 0 || this.groupResults.length > 0
      } catch (e) {
        console.error('Contact search failed:', e)
      }
      this.searchingContacts = false
    },

    selectContact(contact, field = null) {
      const target = field || this.activeRecipientField || 'to'
      const list = this._recipientsFor(target)
      // Add to recipients if not already present
      if (!list.some(r => r.email === contact.email)) {
        list.push({ ...contact, kind: 'contact' })
      }
      this._clearTypedBufferFor(target)
      if (target === 'to') {
        if (!this.selectedContacts.some(c => c.email === contact.email)) {
          this.selectedContacts.push(contact)
        }
        this.searchContactProperties(contact)
      }
      this.showContactDropdown = false
      this.contactResults = []
      this.groupResults = []
      this.touchDraft()
    },

    selectGroup(group) {
      // Groups are only offered on the To field — see searchContacts().
      if (!this.composerToRecipients.some(r => r.kind === 'group' && r.id === group.id)) {
        this.composerToRecipients.push({
          kind: 'group',
          id: group.id,
          label: group.label,
          count: group.count,
          // No email — used by the chip key + dedup against contacts
          email: `__group__${group.id}`,
          recipients: null,         // populated lazily on first modal open
          excludedKeys: []          // serialised set of "kind:id" the user has unticked
        })
      }
      this.composerTo = ''
      this.showContactDropdown = false
      this.contactResults = []
      this.groupResults = []
      this.touchDraft()
    },

    // The badge count for a group chip — net of any deselections.
    groupChipCount(chip) {
      if (chip.recipients) {
        return chip.recipients.length - (chip.excludedKeys?.length || 0)
      }
      return chip.count
    },

    // Open the group modal for the chip at the given index. Loads the
    // resolved recipient list on first open and snapshots the current
    // exclusion state so Cancel can revert.
    async openGroupModal(idx) {
      const chip = this.composerToRecipients[idx]
      if (!chip || chip.kind !== 'group') return

      this.groupModalChipIdx = idx
      this.groupModalSearch = ''
      this._groupModalSnapshot = [...(chip.excludedKeys || [])]
      this.groupModalOpen = true

      if (!chip.recipients) {
        this.groupModalLoading = true
        try {
          const resp = await fetch(`/api/email_groups/recipients?id=${encodeURIComponent(chip.id)}`, {
            headers: { 'Accept': 'application/json' }
          })
          if (resp.ok) {
            const data = await resp.json()
            chip.recipients = data.recipients
            // Keep the cached count in sync with the resolved list
            chip.count = data.recipients.length
          } else {
            console.error('Failed to load group recipients:', await resp.text())
          }
        } catch (e) {
          console.error('Failed to load group recipients:', e)
        }
        this.groupModalLoading = false
      }
    },

    closeGroupModal(commit) {
      if (!commit && this.groupModalChipIdx != null) {
        // Revert to snapshot
        const chip = this.composerToRecipients[this.groupModalChipIdx]
        if (chip) chip.excludedKeys = this._groupModalSnapshot || []
      }
      this.groupModalOpen = false
      this.groupModalChipIdx = null
      this.groupModalSearch = ''
      this._groupModalSnapshot = null
    },

    get groupModalChip() {
      if (this.groupModalChipIdx == null) return null
      return this.composerToRecipients[this.groupModalChipIdx]
    },

    get groupModalFilteredRecipients() {
      const chip = this.groupModalChip
      if (!chip || !chip.recipients) return []
      const q = (this.groupModalSearch || '').trim().toLowerCase()
      if (!q) return chip.recipients
      return chip.recipients.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q)
      )
    },

    get groupModalSelectedCount() {
      const chip = this.groupModalChip
      if (!chip || !chip.recipients) return 0
      return chip.recipients.length - (chip.excludedKeys?.length || 0)
    },

    _recipientKey(recipient) {
      return `${recipient.kind}:${recipient.id}`
    },

    isGroupRecipientSelected(recipient) {
      const chip = this.groupModalChip
      if (!chip) return false
      return !(chip.excludedKeys || []).includes(this._recipientKey(recipient))
    },

    toggleGroupRecipient(recipient) {
      const chip = this.groupModalChip
      if (!chip) return
      const key = this._recipientKey(recipient)
      const excluded = chip.excludedKeys || []
      if (excluded.includes(key)) {
        chip.excludedKeys = excluded.filter(k => k !== key)
      } else {
        chip.excludedKeys = [...excluded, key]
      }
    },

    selectAllInGroup() {
      const chip = this.groupModalChip
      if (!chip) return
      chip.excludedKeys = []
    },

    deselectAllInGroup() {
      const chip = this.groupModalChip
      if (!chip || !chip.recipients) return
      chip.excludedKeys = chip.recipients.map(r => this._recipientKey(r))
    },

    deselectAllOrganisationsInGroup() {
      const chip = this.groupModalChip
      if (!chip || !chip.recipients) return
      const orgKeys = chip.recipients
        .filter(r => r.person_type === 'organisation')
        .map(r => this._recipientKey(r))
      const existing = chip.excludedKeys || []
      // Add org keys that aren't already excluded
      chip.excludedKeys = [...new Set([...existing, ...orgKeys])]
    },

    // === Mass email: variable insertion ===

    get hasGroupChips() {
      return this.composerToRecipients.some(r => r.kind === 'group')
    },

    // The Trix editor currently shown in the active composer mode.
    _activeTrixEditor() {
      const ref = this.composing ? this.$refs.composeTrixEditor : this.$refs.replyTrixEditor
      return ref?.editor || null
    },

    // Insert a {{variable}} placeholder at the current Trix cursor.
    // Use mousedown.prevent on the calling button so the editor doesn't
    // lose its selection when you click the pill.
    insertVariableAtCursor(name) {
      const editor = this._activeTrixEditor()
      if (!editor) return
      editor.recordUndoEntry(`Insert ${name}`)
      editor.insertString(`{{${name}}}`)
    },

    // === Mass email: send flow ===

    // Total recipients across all chips. Group chip counts honour any
    // per-chip deselections from the chip-click modal. Note that this
    // can overcount if the same person appears in multiple selected
    // groups — the server dedupes by email and the success toast shows
    // the actual count.
    get totalMassEmailRecipientCount() {
      return this.composerToRecipients.reduce((sum, chip) => {
        if (chip.kind === 'group') return sum + this.groupChipCount(chip)
        return sum + 1
      }, 0)
    },

    openSendConfirmation() {
      this.sendConfirmCount = this.totalMassEmailRecipientCount
      this.sendConfirmOpen = true
    },

    cancelMassEmailSend() {
      if (this.sendingMassEmail) return
      this.sendConfirmOpen = false
    },

    // POST to /inbox/send_mass_email with the chip state, then close
    // the slideover and reset the composer on success.
    async confirmMassEmailSend() {
      if (this.sendingMassEmail) return
      this.sendingMassEmail = true
      try {
        // Same as sendReply: settle any in-flight autosave so draft_id is
        // accurate — the server deletes the draft as part of the send.
        clearTimeout(this._autosaveTimer)
        if (this._saveChain) await this._saveChain.catch(() => {})

        const groups = this.composerToRecipients
          .filter(r => r.kind === 'group')
          .map(chip => {
            // If the chip's modal was opened, the recipient list is loaded
            // and we can send the explicit selected ID list. Otherwise,
            // selected_ids: null tells the server "include everyone in
            // this group".
            let selected_ids = null
            if (chip.recipients) {
              selected_ids = chip.recipients
                .filter(r => !(chip.excludedKeys || []).includes(`${r.kind}:${r.id}`))
                .map(r => r.id)
            }
            return { id: chip.id, selected_ids: selected_ids }
          })

        // Free-text and individual contact chips become extra_emails.
        // The mass email backend doesn't need to know they came from a
        // Person/Landlord — it just sends to that address.
        const extra_emails = this.composerToRecipients
          .filter(r => r.kind !== 'group' && r.email)
          .map(r => r.email)

        const payload = {
          subject: this.composerSubject,
          body: this.replyDraft,
          groups: groups,
          extra_emails: extra_emails,
          attachments: await this.prepareAttachments(),
          draft_id: this.currentDraftId || null
        }

        const resp = await fetch('/inbox/send_mass_email', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })

        const data = await resp.json()
        if (resp.ok && data.success) {
          this.sendConfirmOpen = false
          this.sendingMassEmail = false
          this._resetDraftTracking()
          if (this.currentDraftId) {
            const deletedDraftId = this.currentDraftId
            this.currentDraftId = null
            window.dispatchEvent(new CustomEvent('drafts-changed', { detail: { delta: -1, deletedDraftId } }))
          }
          this._forceClose()
        } else {
          this.sendingMassEmail = false
          alert(data.error || 'Failed to send mass email')
        }
      } catch (e) {
        console.error('Mass email send failed:', e)
        this.sendingMassEmail = false
        alert('Failed to send mass email. Please try again.')
      }
    },

    removeRecipient(index, field = 'to') {
      const list = this._recipientsFor(field)
      const removed = list.splice(index, 1)[0]
      if (field === 'to' && removed?.email) {
        this.selectedContacts = this.selectedContacts.filter(c => c.email !== removed.email)
        delete this._contactPropertyCache[removed.email]
        this._recomputeMatchedProperties()
      }
      this.touchDraft()
    },

    // Add a raw email address typed by the user (on Enter or comma)
    addTypedRecipient(field = 'to') {
      const email = this._typedBufferFor(field).trim().replace(/,$/, '').trim()
      if (!email) return false
      // Basic email validation
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false
      const list = this._recipientsFor(field)
      if (!list.some(r => r.email === email)) {
        list.push({ kind: 'contact', email, name: email, type: null, id: null })
      }
      this._clearTypedBufferFor(field)
      this.touchDraft()
      return true
    },

    contactTypeLabel(type) {
      const labels = { landlord: 'Landlord', contractor: 'Contractor', person: 'Contact', user: 'Staff' }
      return labels[type] || type
    },

    onSubjectInput() {
      this.touchDraft()
      clearTimeout(this._propertySearchTimeout)

      // Re-resolve each selected contact's property against the new subject
      this.selectedContacts.forEach(c => this._resolveContactProperty(c))
      this._recomputeMatchedProperties()

      const query = this.composerSubject.trim()
      if (query.length < 3) {
        if (this._subjectMatchedProperty) {
          this._subjectMatchedProperty = null
          this._recomputeMatchedProperties()
        }
        return
      }
      // Subject-only fallback: only useful when no contact has resolved a property
      this._propertySearchTimeout = setTimeout(() => {
        if (this.matchedProperties.length === 0) this.searchProperty(query)
      }, 400)
    },

    async searchProperty(query) {
      try {
        // The address the subject names, and only when it names exactly one
        // property — never the free-text search, whose typo-tolerant
        // fallback scored a whole subject line against every address and
        // linked an unrelated house.
        const resp = await fetch(`/api/properties/match_subject?q=${encodeURIComponent(query)}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (resp.ok) {
          const results = await resp.json()
          this._subjectMatchedProperty = results.length > 0 ? results[0] : null
          this._recomputeMatchedProperties()
        }
      } catch (e) { console.error('Property search failed:', e) }
    },

    async searchContactProperties(contact) {
      // For people/landlords/contractors, check if they have linked properties
      if (!contact) return
      const entityType = contact.type
      const entityId = contact.id?.toString().replace(/^(landlord_|contractor_)/, '')
      if (!entityId) return

      try {
        const resp = await fetch(`/inbox/contact_properties?type=${entityType}&id=${entityId}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (resp.ok) {
          this._contactPropertyCache[contact.email] = await resp.json()
          this._resolveContactProperty(contact)
          this._recomputeMatchedProperties()
        }
      } catch (e) { console.error('Contact properties search failed:', e) }
    },

    // Pick the best property for `contact` from its cached property list,
    // either auto-linking the only one or matching the subject when there
    // are several. Result is stored on `contact._resolvedProperty`.
    _resolveContactProperty(contact) {
      const properties = this._contactPropertyCache[contact.email] || []
      if (properties.length === 0) {
        contact._resolvedProperty = null
        return
      }

      if (properties.length === 1) {
        contact._resolvedProperty = { ...properties[0], _fromContact: true }
        if (!this.composerSubject.trim() && properties[0].display_name) {
          this.composerSubject = properties[0].display_name + ' - '
        }
        return
      }

      const subject = this.composerSubject.trim().toLowerCase()
      if (subject.length < 3) {
        contact._resolvedProperty = null
        return
      }

      const match = properties.find(p =>
        p.display_name?.toLowerCase().includes(subject) ||
        subject.includes(p.display_name?.toLowerCase()) ||
        p.full_address?.toLowerCase().includes(subject) ||
        subject.includes(p.full_address?.toLowerCase()?.split(',')[0])
      )
      contact._resolvedProperty = match ? { ...match, _fromContact: true } : null
    },

    _recomputeMatchedProperties() {
      const seen = new Set()
      const out = []
      for (const c of this.selectedContacts) {
        const p = c._resolvedProperty
        if (p && !seen.has(p.id)) {
          seen.add(p.id)
          out.push(p)
        }
      }
      // Subject-search fallback only kicks in when no contact contributed
      if (out.length === 0 && this._subjectMatchedProperty) {
        out.push(this._subjectMatchedProperty)
      }
      this.matchedProperties = out
    }
  }
}
